import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import api from "../../../services/apiClient";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import { useVisitTracking } from "../../../context/VisitTrackingContext";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import SimpleCircularUpload from "../../common/SimpleCircularUpload";
import UserProfileStaffDocuments from "./UserProfileStaffDocuments";
import ExternalAssignedStudiesPanel from "./ExternalAssignedStudiesPanel";
import Dropdown from "../../common/dropDown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GenderSelector from "../../common/GenderSelector";
import { normListId } from "../../common/dualListBox";
import StudyAssignmentDualList, {
  mapStudyOptionsToAssignedRows,
} from "./StudyAssignmentDualList";
import { filterAssignablePermissionKeys } from "../../../constants/permissions";
import { expandStoredQmsPermissionKeys } from "../qms/qmsPermissionUtils";
import { expandStoredIsfPermissionKeys } from "../../../constants/isfPermissionConsolidation";
import { sanitizeSelectedPermissionsForSave } from "../../../constants/qmsPermissions";
import {
  parsePermissions,
  hasPermission,
  isSystemAdministratorRole as isSystemAdministratorRoleName,
  getSessionRoleName,
  getAllPermissionKeys,
  getEffectivePermissionKeys,
} from "../../../utils/permissionUtils";
import "./userProfile.scss";
import {
  validateNationalMobile,
  sanitizeNationalMobileInput,
  normalizeNationalMobileInput,
  orgMobileDialLabel,
} from "../../../utils/siteFieldValidationUtils";
import {
  fetchOrganizationSetup,
  parseOrganizationSetupResponse,
} from "../../../services/organizationSetupService";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import ConfirmModal from "../../common/ConfirmModal";
import AuthModal from "../../common/authmodal/authmodal";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import StaffLoginIpSection from "./StaffLoginIpSection";
import InternalUserAddEditModal from "./InternalUserAddEditModal";
import AdminChangeUserCredentialsModal from "../CredentialPolicy/AdminChangeUserCredentialsModal";
import UserStaffDocumentAccessModal from "./UserStaffDocumentAccessModal";
import {
  useExternalUserAccessScoping,
  parseCommaSeparatedIds,
  parseExternalEntityAccessRows,
  extractExternalEntityAccessFromProfile,
  fetchExternalEntityAccessGrants,
} from "./ExternalUserAccessScoping";
import {
  formatUserTypeLabel,
  isSponsorCroUserRecord,
  isSponsorOrCroRoleLabel,
} from "../../../constants/userTypeLabels";
import {
  getActiveSiteLookup,
  siteToDropdownOptions,
} from "../../../services/lookupService";
import {
  adminChangeUserAccessCode,
  adminChangeUserPassword,
  parseAdminCredentialChangeResponse,
} from "../../../services/credentialPolicyService";
import {
  getMfaSettings,
  isAuthenticatorEnrolled,
  resetAuthenticator,
} from "../../../services/mfaService";
import { fetchStudyMgmtList } from "../studymanagement/studyMgmtListService";
import {
  buildStudyColorLookup,
  pickStudyColorRaw,
} from "../../../utils/studyColorUtils";
import {
  organizationHasQmsModule,
  organizationHasQmsTrainingAdditionalRolesFeature,
} from "../../../utils/orgModuleAccess";
import { hasQmsActionPermission } from "../qms/qmsPermissionUtils";
import {
  fetchStaffDocumentLibraryDocuments,
  fetchStaffMyLearningDocuments,
} from "../qms/qmsService";
import {
  apiMessage,
  firstSuccessStoryRow,
  isApiSuccess,
} from "../qms/qmsApiUtils";

/** Profile GetProfileDetails exposes Inactive as `Archived`. */
function isStaffProfileActive(staff) {
  if (!staff) return true;
  const archived = staff.Archived ?? staff.Inactive ?? staff.inactive;
  if (
    archived === 1 ||
    archived === true ||
    archived === "1" ||
    String(archived).trim().toLowerCase() === "yes"
  ) {
    return false;
  }
  const status = String(staff.Status ?? staff.AccountStatus ?? "")
    .trim()
    .toLowerCase();
  if (
    status === "inactive" ||
    status === "blocked" ||
    status === "archived"
  ) {
    return false;
  }
  return true;
}

const POPOVER_WIDTH = 240;
const POPOVER_VIEWPORT_PAD = 8;

/** Fixed portal popover anchored near the edit control (not screen-centered). */
function ProfileInlinePopover({
  open,
  anchorRef,
  children,
  "aria-label": ariaLabel,
  width: widthProp = POPOVER_WIDTH,
  estimatedHeight = 220,
  /** bottom | right — address uses right so it stays beside the sidebar */
  placement = "bottom",
  className = "",
}) {
  const popoverRef = useRef(null);
  const [style, setStyle] = useState({
    top: 0,
    left: 0,
    width: widthProp,
    maxHeight: "none",
  });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const measuredHeight =
      popoverRef.current?.offsetHeight || estimatedHeight;
    const width = Math.min(
      widthProp,
      window.innerWidth - POPOVER_VIEWPORT_PAD * 2,
    );
    const maxHeight = Math.max(
      180,
      window.innerHeight - POPOVER_VIEWPORT_PAD * 2,
    );
    const height = Math.min(measuredHeight, maxHeight);

    let left;
    let top;

    if (placement === "right") {
      left = rect.right + 8;
      if (left + width > window.innerWidth - POPOVER_VIEWPORT_PAD) {
        left = Math.max(
          POPOVER_VIEWPORT_PAD,
          rect.left - width - 8,
        );
      }
      top = rect.top;
      if (top + height > window.innerHeight - POPOVER_VIEWPORT_PAD) {
        top = Math.max(
          POPOVER_VIEWPORT_PAD,
          window.innerHeight - POPOVER_VIEWPORT_PAD - height,
        );
      }
      top = Math.max(POPOVER_VIEWPORT_PAD, top);
    } else {
      left = rect.left;
      left = Math.max(
        POPOVER_VIEWPORT_PAD,
        Math.min(left, window.innerWidth - width - POPOVER_VIEWPORT_PAD),
      );
      const spaceBelow = window.innerHeight - rect.bottom - POPOVER_VIEWPORT_PAD;
      const spaceAbove = rect.top - POPOVER_VIEWPORT_PAD;
      if (spaceBelow >= height + 6 || spaceBelow >= spaceAbove) {
        top = rect.bottom + 6;
      } else {
        top = Math.max(POPOVER_VIEWPORT_PAD, rect.top - height - 6);
      }
      if (top + height > window.innerHeight - POPOVER_VIEWPORT_PAD) {
        top = Math.max(
          POPOVER_VIEWPORT_PAD,
          window.innerHeight - POPOVER_VIEWPORT_PAD - height,
        );
      }
    }

    setStyle({
      top,
      left,
      width,
      maxHeight,
    });
  }, [anchorRef, widthProp, estimatedHeight, placement]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const frame = requestAnimationFrame(() => updatePosition());
    const onReposition = () => updatePosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, updatePosition, children]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={popoverRef}
      className={[
        "profile-inline-edit__popover",
        "profile-inline-edit__popover--portal",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      role="dialog"
      aria-label={ariaLabel}
      style={style}
    >
      {children}
    </div>,
    document.body,
  );
}

const parseMappedSiteIds = (staff) => {
  const raw = staff?.MappedSiteIds ?? staff?.SiteIdRef ?? "";
  return String(raw)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
};

const isSystemAdministratorRole = (role) => {
  const label =
    typeof role === "object" && role !== null ? role.label : role ?? "";
  return String(label).trim().toLowerCase() === "system administrator";
};

const getRoleIdValue = (role) => {
  if (typeof role === "object" && role !== null) return String(role.value ?? "").trim();
  return String(role ?? "").trim();
};

const mapAdditionalTrainingRolesFromApi = (rows, roleOptions) => {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map((row) => {
      const value = String(row?.RoleId ?? row?.roleId ?? "").trim();
      if (!value) return null;
      const match = (roleOptions || []).find(
        (r) => String(r.value).toLowerCase() === value.toLowerCase(),
      );
      return match || { value, label: row?.Roles ?? row?.roles ?? value };
    })
    .filter(Boolean);
};

const buildAdditionalTrainingRoleOptions = (roleOptions, primaryRole) => {
  const primaryId = getRoleIdValue(primaryRole).toLowerCase();
  return (roleOptions || []).filter(
    (r) => String(r.value).toLowerCase() !== primaryId,
  );
};

const formatAdditionalTrainingRoleIdsForSave = (selected) =>
  (selected || [])
    .map((r) => String(r?.value ?? "").trim())
    .filter(Boolean)
    .join(",");

/** Match study rows from API (raw) or dual-list option shape { value, label }. */
const pickStudyIdFromRow = (s) => {
  if (!s || typeof s !== "object") return "";
  return (
    s.StudyId ??
    s.studyId ??
    s.StudyIdRef ??
    s.studyIdRef ??
    s.StudyID ??
    s.studyID ??
    s.Study_Id ??
    s.Id ??
    s.id ??
    s.GUID ??
    s.guid ??
    s.value ??
    ""
  );
};

/**
 * GetProfileDetails may put assigned studies on `data.StudyList`, on `StaffRecord[0]`, or under alternate keys.
 */
const collectAssignedStudyRowsFromProfile = (data, staff) => {
  const chunks = [
    data?.StudyList,
    data?.studyList,
    data?.AssignedStudyList,
    data?.UserStudyList,
    data?.StaffStudyList,
    staff?.StudyList,
    staff?.AssignedStudyList,
  ];
  const flat = [];
  for (const c of chunks) {
    if (Array.isArray(c)) flat.push(...c);
  }
  const byId = new Map();
  for (const row of flat) {
    const sid = pickStudyIdFromRow(row);
    if (!sid) continue;
    const k = normListId(sid);
    if (!k) continue;
    if (!byId.has(k)) byId.set(k, row);
  }
  return [...byId.values()];
};

/**
 * Internal user profile: align assigned rows with org StudyList options so
 * StudyAssignmentDualList selected ids match catalog `options[].value`.
 */
const mergeInternalStudiesWithCommonOptions = (rawRows, commonStudyOptions) => {
  if (!Array.isArray(rawRows) || !rawRows.length) return [];
  const seen = new Set();
  const out = [];
  for (const row of rawRows) {
    const sid = pickStudyIdFromRow(row);
    if (!sid) continue;
    const key = normListId(sid);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const opt = (commonStudyOptions || []).find(
      (o) => normListId(o.value) === key,
    );
    const studyNumber =
      row.StudyNumber ||
      row.studyNumber ||
      row.StudyCode ||
      opt?.studyNumber ||
      "";
    const studyName =
      row.StudyName ?? row.studyName ?? opt?.studyName ?? opt?.label ?? "";
    const color =
      pickStudyColorRaw(row) || pickStudyColorRaw(opt) || "";
    const label =
      opt?.label ||
      (studyNumber ? `${studyNumber} - ${studyName}` : studyName || String(sid));
    out.push({
      StudyId: opt?.value ?? sid,
      StudyName: studyName,
      value: opt?.value ?? sid,
      label,
      studyNumber,
      studyName,
      studyColor: color,
      StudySchColor: color,
      StudyColor: color,
    });
  }
  return out;
};

const UserProfile = ({
  variant = "internal",
  /** When set (e.g. full-width modal), use this instead of route :staffId */
  embeddedStaffId = null,
  embedded = false,
  onClose = null,
}) => {
  const { staffId: routeStaffId } = useParams();
  const staffId = String(embeddedStaffId || routeStaffId || "").trim();
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { updateRecentVisitSubtitle } = useVisitTracking();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [activeTab, setActiveTab] = useState(
    variant === "external" ? "studies" : "general",
  );
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [lineManagers, setLineManagers] = useState([]);
  const [studyList, setStudyList] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [externalIsfStudiesCsv, setExternalIsfStudiesCsv] = useState("");
  const [externalIsfDocumentsCsv, setExternalIsfDocumentsCsv] = useState("");
  /** Same payload you save as DocumentsXML — used when ExternalStaffISFDocuments CSV is not returned */
  const [externalStaffDocumentsXml, setExternalStaffDocumentsXml] =
    useState("");
  const [externalShareHydrationNonce, setExternalShareHydrationNonce] =
    useState(0);
  /** userResponse.data.ExternalStaffISFDocuments as array of { StudyId, Category, DocumentIds } */
  const [externalStaffIsfDocumentsFromApi, setExternalStaffIsfDocumentsFromApi] =
    useState(null);
  const [externalEntityAccessFromApi, setExternalEntityAccessFromApi] =
    useState(null);
  const [assignedTableBootstrapping, setAssignedTableBootstrapping] =
    useState(true);
  const [nameEditOpen, setNameEditOpen] = useState(false);
  const [genderEditOpen, setGenderEditOpen] = useState(false);
  const [contactEditOpen, setContactEditOpen] = useState(false);
  const [dobEditOpen, setDobEditOpen] = useState(false);
  const [addressEditOpen, setAddressEditOpen] = useState(false);
  const [roleEditOpen, setRoleEditOpen] = useState(false);
  const [departmentEditOpen, setDepartmentEditOpen] = useState(false);
  const [roleEditDraft, setRoleEditDraft] = useState(null);
  const [departmentEditDraft, setDepartmentEditDraft] = useState(null);
  const [nameEditDraft, setNameEditDraft] = useState({
    firstName: "",
    lastName: "",
    employeeNumber: "",
  });
  const [contactEditDraft, setContactEditDraft] = useState({
    contactNumber: "",
  });
  const [dobEditDraft, setDobEditDraft] = useState(null);
  const [addressEditDraft, setAddressEditDraft] = useState({
    addressLine1: "",
    postalCode: "",
    city: "",
    county: "",
    country: "",
  });
  const nameEditAnchorRef = useRef(null);
  const contactEditAnchorRef = useRef(null);
  const dobEditAnchorRef = useRef(null);
  const addressEditAnchorRef = useRef(null);
  const roleEditAnchorRef = useRef(null);
  const departmentEditAnchorRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [orgCountryIso2, setOrgCountryIso2] = useState("GB");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    contactNumber: "",
    mobileCountryIso2: "GB",
    profilePicture: "",
    prefix: "EMP",
    employeeNumber: "00000",
    email: "",
    confirmEmail: "",
    dob: null,
    startDate: null,
    accessDate: null,
    gender: "",
    role: null,
    department: null,
    lineManager: null,
    haveLineManagerRights: false,
    addressLine1: "",
    postalCode: "",
    city: "",
    county: "",
    country: "",
    studies: [],
    selectedSites: [],
    qmsTrainingAdditionalRoles: [],
    // External (CRO) user — 1 = external, 0 = internal
    IsCRO: 0,
  });

  const isExternalProfile =
    variant === "external" || isSponsorCroUserRecord(formData.IsCRO);

  const [profileData, setProfileData] = useState({
    profilePicture: "",
    fullName: "",
    role: "",
    department: "",
    lastLogin: "",
  });

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [userRoleId, setUserRoleId] = useState("");
  const [eSignatureDataUrl, setESignatureDataUrl] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureMode, setSignatureMode] = useState("draw"); // "draw" | "type"
  const [typedSignature, setTypedSignature] = useState("");
  const signatureCanvasRef = useRef(null);
  const fetchStartedRef = useRef(false);
  /** Studies selected before switching to System Administrator (restored when leaving that role). */
  const studiesBeforeSysAdminRef = useRef(null);
  /** Last server-saved form values — used so section Update only persists that section. */
  const savedFormSnapshotRef = useRef(null);
  /** Last saved role + permissions (before unsaved edits). */
  const savedPermissionsRef = useRef([]);
  const savedRoleIdRef = useRef("");
  const [roleChangeConfirm, setRoleChangeConfirm] = useState({
    open: false,
    nextRole: null,
  });
  const [permissionSaveConfirmOpen, setPermissionSaveConfirmOpen] =
    useState(false);
  /** False when profile user is deactivated (Archived / Inactive). */
  const [isProfileUserActive, setIsProfileUserActive] = useState(true);
  const [adminCredForm, setAdminCredForm] = useState({ open: false, row: null });
  const [adminCredConfirm, setAdminCredConfirm] = useState({
    open: false,
    mode: null,
    row: null,
    value: "",
  });
  const [adminCredAuthOpen, setAdminCredAuthOpen] = useState(false);
  const [pendingAdminCred, setPendingAdminCred] = useState(null);
  const [docAccessModal, setDocAccessModal] = useState({
    open: false,
    mode: "library",
    row: null,
  });
  const [mfaResetConfirmOpen, setMfaResetConfirmOpen] = useState(false);
  const [mfaResetAuthOpen, setMfaResetAuthOpen] = useState(false);
  /** Only users with a registered authenticator can be reset. */
  const [hasAuthenticatorEnrolled, setHasAuthenticatorEnrolled] =
    useState(false);
  const isOwnProfile =
    String(userData?.StaffId || "").toLowerCase() ===
    String(staffId || "").toLowerCase();
  /** Admin editing another Sponsor/CRO user — grant assignment only. */
  const isExternalAdminView = isExternalProfile && !isOwnProfile;

  // Admin sections (Additional Info / Studies / Role & Permissions + Update):
  // User Management permission OR System Administrator role.
  // Basic info (photo / name / gender): own profile OR the same admin access.
  // Signature: own profile only (existing rule).
  // Inactive profile users are view-only — no edits or admin actions.
  const isSystemAdmin = isSystemAdministratorRoleName(getSessionRoleName(userData));

  const loggedInPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(
        userData?.Permission ||
          userData?.UserPermission ||
          userData?.Permissions ||
          userData?.PermissionString ||
          "",
      );

  const canManageUsers =
    isSystemAdmin ||
    hasPermission(loggedInPermissions, "USER_MANAGEMENT") ||
    hasPermission(loggedInPermissions, "UPDATE_USER");

  const canChangeCredentials =
    !!userData &&
    (isSystemAdmin ||
      hasPermission(loggedInPermissions, "CHANGE_CREDENTIALS"));

  /** Lost-device recovery: clear authenticator so user can re-register. */
  const canResetMfa =
    canChangeCredentials ||
    canManageUsers ||
    isSystemAdmin;

  const orgHasQms = organizationHasQmsModule(userData);
  const canManageLibraryStaffAccess =
    orgHasQms &&
    (isSystemAdmin ||
      hasQmsActionPermission(loggedInPermissions, "QMS_LIBRARY_SEE_STAFF"));
  const canManageMyLearningStaffAccess =
    orgHasQms &&
    (isSystemAdmin ||
      hasQmsActionPermission(loggedInPermissions, "QMS_LIBRARY_ADD_STAFF"));

  const canEditAdminSections = canManageUsers && isProfileUserActive;
  const canEditBasicInfo =
    (isOwnProfile || canManageUsers) && isProfileUserActive;
  /** Own My Documents: editable without User Management; inactive profile → read-only. */
  const canEditDocuments =
    isProfileUserActive && (isOwnProfile || canManageUsers);
  const canEditUserPermissions = canManageUsers && isProfileUserActive;
  const canEditRole =
    (isSystemAdmin ||
      hasPermission(loggedInPermissions, "USER_MANAGEMENT")) &&
    isProfileUserActive;
  const canEditSignature = isOwnProfile && isProfileUserActive;

  const externalScoping = useExternalUserAccessScoping({
    organizationId: userData?.OrganizationId,
    staffId: userData?.StaffId,
    studyOptions: studyList,
    showAlert,
    studies: formData.studies,
    onStudiesChange: (updater) =>
      setFormData((prev) => ({
        ...prev,
        studies:
          typeof updater === "function"
            ? updater(prev.studies || [])
            : updater,
      })),
    readOnly: !isProfileUserActive || !canEditAdminSections,
    highlightedStudyIdsCsv: externalIsfStudiesCsv,
    serverDocumentIdsCsv: externalIsfDocumentsCsv,
    serverDocumentsXml: externalStaffDocumentsXml,
    serverExternalStaffIsfDocuments: isExternalProfile
      ? externalStaffIsfDocumentsFromApi
      : null,
    serverEntityAccess: externalEntityAccessFromApi,
    tableBootstrapping: assignedTableBootstrapping,
    hydrationNonce: isExternalProfile ? externalShareHydrationNonce : 0,
  });

  /** Sidebar / User Management list — only link in breadcrumb when user can open that screen. */
  const canAccessUserManagement =
    isSystemAdmin || hasPermission(loggedInPermissions, "USER_MANAGEMENT");

  const breadcrumbItems = useMemo(() => {
    const items = [{ label: "Home", path: "/notification-center" }];
    if (canAccessUserManagement) {
      items.push({ label: "User Management", path: "/usermanagement" });
    }
    items.push({ label: "User Profile" });
    return items;
  }, [canAccessUserManagement]);

  const visibleRoles = useMemo(() => {
    const list = roles || [];
    if (isExternalProfile) {
      return list.filter((r) => isSponsorOrCroRoleLabel(r?.label));
    }
    return list.filter(
      (r) =>
        String(r?.label || "")
          .trim()
          .toLowerCase() !== "super admin",
    );
  }, [roles, isExternalProfile]);

  const sponsorCroDepartments = useMemo(
    () => departments || [],
    [departments],
  );

  const profileEditFormData = useMemo(() => {
    const emp = String(formData.employeeNumber || "").trim();
    const parts = emp.split(/\s+/);
    const hasSplitPrefix = parts.length > 1;
    return {
      ...formData,
      StaffId: staffId || "",
      prefix: hasSplitPrefix ? parts[0] : formData.prefix || "EMP",
      employeeNumber: hasSplitPrefix ? parts.slice(1).join(" ") : emp,
      userPermissions: filterAssignablePermissionKeys(selectedPermissions || []),
    };
  }, [formData, staffId, selectedPermissions]);

  const handleProfileFormPermissionsChange = (permissions) => {
    setSelectedPermissions(filterAssignablePermissionKeys(permissions || []));
  };

  const orgHasQmsTrainingAdditionalRoles =
    organizationHasQmsTrainingAdditionalRolesFeature(userData);

  const qmsTrainingAdditionalRoleOptions = useMemo(
    () => buildAdditionalTrainingRoleOptions(roles, formData.role),
    [roles, formData.role],
  );

  const canEditPermissionCards =
    Boolean(userRoleId) &&
    profileData.role !== "System Administrator" &&
    canEditAdminSections;

  useEffect(() => {
    setExternalIsfStudiesCsv("");
    setExternalIsfDocumentsCsv("");
    setExternalStaffDocumentsXml("");
    setExternalStaffIsfDocumentsFromApi(null);
    setExternalEntityAccessFromApi(null);
    setAssignedTableBootstrapping(true);
    setExternalShareHydrationNonce(0);
    externalScoping.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset is stable; avoid wiping grants when IsCRO hydrates
  }, [staffId]);

  useEffect(() => {
    const orgId = userData?.OrganizationId;
    const uid = userData?.StaffId;
    if (!orgId || !uid || !staffId) return;
    if (fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    fetchUserProfile().finally(() => {
      fetchStartedRef.current = false;
    });
  }, [userData?.OrganizationId, userData?.StaffId, staffId]);

  useEffect(() => {
    if (isExternalProfile && (activeTab === "general" || activeTab === "permissions")) {
      setActiveTab("studies");
    }
    // External admin view: Studies + Documents only (no Profile / Networking).
    if (
      isExternalAdminView &&
      activeTab !== "studies" &&
      activeTab !== "documents"
    ) {
      setActiveTab("studies");
    }
    if (!isExternalProfile && activeTab === "permissions") {
      setActiveTab("general");
    }
  }, [isExternalProfile, isExternalAdminView, activeTab]);

  // Same flow as userManagement edit modal: common data first, then individual user
  const getCommonUserProfileData = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return null;
    try {
      const [response, siteRows, rolePermsResponse, mgmtStudyRows] =
        await Promise.all([
          api.post(API_ENDPOINTS.USER.GET_USER_PROFILE_COMMONDATA, {
            OrganizationId: userData.OrganizationId,
            StaffIDRef: userData.StaffId,
            CreatedBy: userData.StaffId,
            PageName: "UserProfile",
          }),
          getActiveSiteLookup(userData),
          api
            .post(API_ENDPOINTS.ROLES.StaffRolesActivity, {
              OrganizationId: userData.OrganizationId,
              CreatedBy: userData.StaffId,
              ActionType: "GetStaffRoles",
              Roles: "",
              RoleId: "",
            })
            .catch(() => null),
          fetchStudyMgmtList({
            organizationId: userData.OrganizationId,
            staffIdRef: userData.StaffId,
            createdBy: userData.StaffId,
            action: "GetStudyMgmtLiveList",
          }).catch(() => []),
        ]);

      const siteOptionsData = siteToDropdownOptions(siteRows).filter(
        (opt) => opt.value,
      );
      setSiteOptions(siteOptionsData);

      if (response.success === 1) {
        const rolePermissionByName = new Map(
          (rolePermsResponse?.data?.SuccessStory || []).map((item) => [
            String(item.Roles || "").trim().toLowerCase(),
            item.Permission || "",
          ]),
        );
        const rolesData = (response.data.StaffRoleList || []).map((item) => ({
          label: item.Roles,
          value: item.RoleId,
          permission:
            item.Permission ||
            rolePermissionByName.get(
              String(item.Roles || "").trim().toLowerCase(),
            ) ||
            "",
        }));
        const departmentsData = utils.mapToOptions(
          response.data.DepartmentList,
          "DepartmentName",
          "DepartmentId",
        );
        const lineManagersData = utils.mapToOptions(
          response.data.ApprovedLineManager,
          ["FirstName", "LastName"],
          "StaffId",
          " ",
        );
        const studyColorLookup = buildStudyColorLookup(mgmtStudyRows);
        const studyData = (response.data.StudyList || []).map((s) => {
          const num = s?.StudyNumber || s?.StudyCode || s?.StudyNo || "";
          const name = s?.StudyName || "";
          const label = num ? `${num} - ${name}` : name;
          const color =
            pickStudyColorRaw(s) ||
            studyColorLookup.get(normListId(s?.StudyId)) ||
            "";
          return {
            label,
            value: s?.StudyId,
            studyNumber: num,
            studyName: name,
            studyColor: color,
            StudySchColor: color,
            StudyColor: s?.StudyColor || color,
          };
        });
        setRoles(rolesData);
        setDepartments(departmentsData);
        setLineManagers(lineManagersData);
        setStudyList(studyData);
        return {
          roles: rolesData,
          departments: departmentsData,
          lineManagers: lineManagersData,
          studies: studyData,
          siteOptions: siteOptionsData,
        };
      }
    } catch (error) {
      return null;
    }
    return null;
  };

  const fetchUserProfile = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    const useAssignedTableSkeleton =
      variant === "external" || isSponsorCroUserRecord(formData.IsCRO);
    try {
      if (!useAssignedTableSkeleton) {
        showLoader("Loading user profile...");
      }

      // Run profile APIs in parallel for fastest load
      const [commonData, userResponse, orgResponse] = await Promise.all([
        getCommonUserProfileData(),
        api.post(API_ENDPOINTS.USER.GET_INDIVIDUAL_USER, {
          OrganizationId: userData.OrganizationId,
          StaffIDRef: userData.StaffId,
          CreatedBy: userData.StaffId,
          StaffId: staffId,
          Action: "GetProfileDetails",
        }),
        fetchOrganizationSetup(userData.OrganizationId, userData.StaffId).catch(
          () => null,
        ),
      ]);

      if (orgResponse && Number(orgResponse?.success) === 1) {
        const { setupRow } = parseOrganizationSetupResponse(orgResponse);
        const iso = String(setupRow?.CountryIso2 || "GB")
          .trim()
          .toUpperCase();
        setOrgCountryIso2(iso || "GB");
      }

      if (commonData && userResponse?.success === 1) {
        const staff = userResponse.data?.StaffRecord?.[0];
        if (!staff) {
          console.error("Error fetching user profile: StaffRecord missing");
          return;
        }
        const staffIsCro = isSponsorCroUserRecord(staff?.IsCRO ?? staff?.IsCro);
        const extStudiesRaw =
          staff?.ExternalStaffISFStudies ??
          staff?.externalStaffISFStudies ??
          userResponse.data?.ExternalStaffISFStudies ??
          "";

        const extDocsFromData = userResponse.data?.ExternalStaffISFDocuments;
        const extDocsFromStaff =
          staff?.ExternalStaffISFDocuments ??
          staff?.externalStaffISFDocuments;

        const peekStructuredExternalDocs = (v) => {
          if (v == null) return null;
          if (Array.isArray(v)) return v.length ? v : null;
          if (typeof v === "object") {
            const sid = v.StudyId ?? v.studyId;
            if (sid != null && String(sid).trim() !== "") return [v];
          }
          return null;
        };

        const structuredExternalDocs =
          peekStructuredExternalDocs(extDocsFromData) ??
          peekStructuredExternalDocs(extDocsFromStaff);

        const extDocsRaw =
          structuredExternalDocs != null
            ? ""
            : extDocsFromStaff ?? extDocsFromData ?? "";
        const documentsXmlRaw =
          staff?.DocumentsXML ??
          staff?.documentsXML ??
          staff?.DocumentsXml ??
          userResponse.data?.DocumentsXML ??
          userResponse.data?.documentsXML ??
          "";
        let entityAccessRaw = staffIsCro
          ? extractExternalEntityAccessFromProfile(userResponse.data, staff)
          : null;
        if (staffIsCro) {
          try {
            const liveGrants = await fetchExternalEntityAccessGrants({
              organizationId: userData.OrganizationId,
              staffId,
            });
            if (parseExternalEntityAccessRows(liveGrants)?.length) {
              entityAccessRaw = liveGrants;
            }
          } catch {
            // Keep profile-attached grants when GetValid is not deployed yet.
          }
        }
        const parsedEntityAccess = staffIsCro
          ? parseExternalEntityAccessRows(entityAccessRaw)
          : null;
        const extStudyIdTokens = parseCommaSeparatedIds(
          typeof extStudiesRaw === "string"
            ? extStudiesRaw
            : String(extStudiesRaw ?? ""),
        );
        const extStudyNormTokens = extStudyIdTokens
          .map((t) => normListId(t))
          .filter(Boolean);
        const generalStudyList = collectAssignedStudyRowsFromProfile(
          userResponse.data,
          staff,
        );
        const generalStudyNormIds = [
          ...new Set(
            generalStudyList
              .map((s) => normListId(pickStudyIdFromRow(s)))
              .filter(Boolean),
          ),
        ];
        const grantStudyNormIds = (parsedEntityAccess || [])
          .map((row) => normListId(row.studyId))
          .filter(Boolean);
        const mergedStudyNormIds = [
          ...new Set([
            ...generalStudyNormIds,
            ...extStudyNormTokens,
            ...grantStudyNormIds,
          ]),
        ];
        const studiesForForm = !staffIsCro
          ? mergeInternalStudiesWithCommonOptions(
              generalStudyList,
              commonData?.studies,
            )
          : mergedStudyNormIds.length > 0
            ? mergedStudyNormIds.map((id) => {
                const opt = commonData.studies.find(
                  (s) => normListId(s.value) === id,
                );
                const fromGeneral = generalStudyList.find(
                  (s) => normListId(pickStudyIdFromRow(s)) === id,
                );
                const sid =
                  opt?.value ?? pickStudyIdFromRow(fromGeneral) ?? id;
                const studyNumber =
                  opt?.studyNumber ||
                  fromGeneral?.StudyNumber ||
                  fromGeneral?.studyNumber ||
                  "";
                const studyName =
                  opt?.studyName ||
                  fromGeneral?.StudyName ||
                  fromGeneral?.studyName ||
                  "";
                const label =
                  opt?.label ||
                  (studyNumber
                    ? `${studyNumber} - ${studyName}`
                    : studyName || fromGeneral?.label || String(sid));
                const color =
                  opt?.studyColor ||
                  opt?.StudySchColor ||
                  fromGeneral?.studyColor ||
                  fromGeneral?.StudySchColor ||
                  "";
                return {
                  StudyId: sid,
                  label,
                  studyNumber,
                  studyName,
                  StudyNumber: studyNumber,
                  StudyName: studyName,
                  studyColor: color,
                  StudySchColor: color,
                };
              })
            : generalStudyList;

        let loadedPermissions = [];
        if (staff?.UserPermission) {
          const perms = staff.UserPermission.split(",")
            .map((p) => p.trim())
            .filter(Boolean);
          loadedPermissions = expandStoredIsfPermissionKeys(
            expandStoredQmsPermissionKeys(perms),
          );
          setSelectedPermissions(loadedPermissions);
        } else {
          setSelectedPermissions([]);
        }
        savedPermissionsRef.current = loadedPermissions;
        savedRoleIdRef.current = String(staff["RoleIdRef"] || "").trim();
        const role = commonData.roles.find(
          (r) =>
            String(r?.value || "").toLowerCase() ===
            String(staff["RoleIdRef"] || "").toLowerCase(),
        );
        const department = commonData.departments.find(
          (d) =>
            String(d?.value || "").toLowerCase() ===
            String(staff["DepartmentId"] || "").toLowerCase(),
        );
        const lineManager = commonData.lineManagers.find(
          (r) =>
            String(r?.value || "").toLowerCase() ===
            String(staff["LineManagerId"] || "").toLowerCase(),
        );
        const mappedSiteIds = parseMappedSiteIds(staff);
        const availableSites = commonData.siteOptions || siteOptions;
        const selectedSites = availableSites.filter((opt) =>
          mappedSiteIds.some(
            (id) => String(opt.value).toLowerCase() === String(id).toLowerCase(),
          ),
        );
        if (staff["RoleIdRef"]) setUserRoleId(staff["RoleIdRef"]);
        const rawLastLogin =
          staff.LastLogin ||
          staff.LastLoginDateTime ||
          staff.LastLoginDate ||
          staff.SystemDateTime ||
          "";
        setIsProfileUserActive(isStaffProfileActive(staff));
        setProfileData({
          profilePicture: staff.ProfilePictureUrl || "",
          fullName: `${staff.Forename || ""} ${staff.Surname || ""}`.trim(),
          role: role?.label || staff["Roles"] || "",
          department: department?.label || staff["DepartmentName"] || "",
          lastLogin: rawLastLogin
            ? utils.formatDateTimeDisplay(rawLastLogin)
            : "",
        });

        setFormData((prev) => {
          const nextForm = {
            ...prev,
            employeeNumber: String(staff["Employee Number"] ?? "").trim(),
            firstName: staff.Forename?.trim() || "",
            lastName: staff.Surname?.trim() || "",
            mobileCountryIso2: String(
              staff.MobileCountryIso2 || orgCountryIso2 || "GB",
            )
              .trim()
              .toUpperCase(),
            contactNumber: sanitizeNationalMobileInput(
              staff["Contact Number"] || "",
              staff.MobileCountryIso2 || orgCountryIso2 || "GB",
            ),
            profilePicture: staff.ProfilePictureUrl || "",
            email: staff["Email Id"] || "",
            confirmEmail: staff["Email Id"] || "",
            dob: staff["Date of birth"]
              ? new Date(utils.parseDDMMYYYY(staff["Date of birth"]))
              : null,
            startDate: staff["Start Date"] ? new Date(staff["Start Date"]) : null,
            accessDate: staff["Access Date"]
              ? new Date(staff["Access Date"])
              : null,
            gender: staff.Gender || staff.gender || "",
            role: role || null,
            department: department || null,
            lineManager: lineManager || null,
            haveLineManagerRights:
              staff["Line Manager Rights"]?.toLowerCase() === "yes",
            addressLine1: staff["H No"] || "",
            postalCode: staff["Post Code"] || "",
            city: staff.City || "",
            county: staff.County || "",
            country: staff.Country || "",
            studies: studiesForForm,
            selectedSites,
            qmsTrainingAdditionalRoles: mapAdditionalTrainingRolesFromApi(
              userResponse.data?.AdditionalTrainingRoles,
              commonData.roles,
            ),
            IsCRO: isSponsorCroUserRecord(staff?.IsCRO ?? staff?.IsCro) ? 1 : 0,
          };
          savedFormSnapshotRef.current = nextForm;
          return nextForm;
        });
        if (staffIsCro) {
          setExternalEntityAccessFromApi(entityAccessRaw);
          setExternalIsfStudiesCsv(
            typeof extStudiesRaw === "string"
              ? extStudiesRaw
              : String(extStudiesRaw ?? ""),
          );
          if (structuredExternalDocs) {
            setExternalStaffIsfDocumentsFromApi(structuredExternalDocs);
            setExternalIsfDocumentsCsv("");
          } else {
            setExternalStaffIsfDocumentsFromApi(null);
            setExternalIsfDocumentsCsv(
              typeof extDocsRaw === "string"
                ? extDocsRaw
                : String(extDocsRaw ?? ""),
            );
          }
          setExternalStaffDocumentsXml(
            typeof documentsXmlRaw === "string"
              ? documentsXmlRaw
              : String(documentsXmlRaw ?? ""),
          );
          setExternalShareHydrationNonce((n) => n + 1);
        }
        const empNumber =
          (staff["Employee Number"] || "").toString().trim() || staffId;
        const fullName = [staff.Forename, staff.Surname]
          .filter(Boolean)
          .join(" ")
          .trim();
        const subtitle = fullName ? `${empNumber} - ${fullName}` : empNumber;
        if (!embedded) {
          updateRecentVisitSubtitle(`/userprofile/${staffId}`, subtitle);
        }
        const signatureValue = staff.ESignature || staff.SignatureBase64;
        if (signatureValue) {
          setESignatureDataUrl(signatureValue);
          if (
            String(userData?.StaffId || "").toLowerCase() ===
            String(staffId || "").toLowerCase()
          ) {
            try {
              sessionStorage.setItem("SignatureBase64", signatureValue);
            } catch (e) {
              console.error("Error storing signature in session:", e);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      if (!useAssignedTableSkeleton) {
        hideLoader();
      }
      setAssignedTableBootstrapping(false);
    }
  };

  const handleFormInput = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getIsCroForApi = () => {
    const v = formData?.IsCRO;
    if (v === 1 || v === 2 || v === true) return 1;
    if (v === 0 || v === false) return 0;
    const s = String(v ?? "").trim().toLowerCase();
    if (s === "1" || s === "2" || s === "true") return 1;
    return 0;
  };

  const isSystemAdministratorProfile =
    isSystemAdministratorRole(formData.role) ||
    profileData.role === "System Administrator";

  const handleStudiesChange = useCallback((selected) => {
    setFieldErrors((prev) => {
      if (!prev?.studies) return prev;
      const next = { ...prev };
      delete next.studies;
      return next;
    });
    setFormData((prev) => ({
      ...prev,
      studies: selected || [],
    }));
  }, []);

  const getProfileValidationErrorMap = (data = formData) => {
    const errors = {};
    const trim = (v) => String(v ?? "").trim();

    if (!trim(data.firstName)) errors.firstName = "First name is required.";
    if (!trim(data.lastName)) errors.lastName = "Last name is required.";

    const mobileCountryIso2 = String(
      data.mobileCountryIso2 || orgCountryIso2 || "GB",
    )
      .trim()
      .toUpperCase();
    const contactResult = validateNationalMobile(data.contactNumber, {
      countryIso2: mobileCountryIso2,
      required: true,
    });
    if (!contactResult.valid) {
      errors.contactNumber = contactResult.message;
    }

    const email = trim(data.email);
    if (!email) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Enter a valid email address.";
    }

    const empNo = trim(data.employeeNumber);
    if (!empNo || empNo === "00000") {
      errors.employeeNumber = "Employee number is required.";
    }

    const roleId = data.role?.value || data.role || "";
    if (!String(roleId).trim()) errors.role = "Role is required.";

    if (!isExternalProfile && getIsCroForApi() === 0 && !isSystemAdministratorProfile) {
      const selected = data.selectedSites || [];
      if (!Array.isArray(selected) || selected.length === 0) {
        errors.selectedSites = "At least one site is required.";
      }
    }

    return errors;
  };

  const getProfileValidationErrors = (data = formData) =>
    Object.values(getProfileValidationErrorMap(data));

  const buildSectionSaveForm = (section, currentForm) => {
    const baseline = savedFormSnapshotRef.current || currentForm;
    if (section === "additional") {
      return {
        ...baseline,
        department: currentForm.department,
        selectedSites: currentForm.selectedSites,
        qmsTrainingAdditionalRoles: currentForm.qmsTrainingAdditionalRoles,
      };
    }
    if (section === "address") {
      return {
        ...baseline,
        addressLine1: currentForm.addressLine1,
        postalCode: currentForm.postalCode,
        city: currentForm.city,
        county: currentForm.county,
        country: currentForm.country,
      };
    }
    if (section === "studies") {
      // Only study assignment changes; keep every other field from last saved snapshot.
      return {
        ...baseline,
        studies: Array.isArray(currentForm.studies)
          ? currentForm.studies
          : baseline.studies || [],
      };
    }
    if (section === "permissions") {
      return {
        ...baseline,
        role: currentForm.role,
        ...(isExternalProfile
          ? { department: currentForm.department }
          : {}),
      };
    }
    return currentForm;
  };

  const formatAddressSummary = (data = formData) => {
    const parts = [
      data.addressLine1,
      data.city,
      data.postalCode,
      data.county,
      data.country,
    ]
      .map((part) => String(part || "").trim())
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "N/A";
  };

  const getSectionValidationErrorMap = (section, data = formData) => {
    const errors = {};
    if (section === "additional") {
      if (
        !isExternalProfile &&
        getIsCroForApi() === 0 &&
        !isSystemAdministratorProfile
      ) {
        const selected = data.selectedSites || [];
        if (!Array.isArray(selected) || selected.length === 0) {
          errors.selectedSites = "At least one site is required.";
        }
      }
      return errors;
    }
    if (section === "address") {
      return errors;
    }
    if (section === "studies") {
      if (isExternalAdminView && getIsCroForApi() === 1) {
        const shareError = externalScoping.getStudyShareValidationError();
        if (shareError) errors.studies = shareError;
      }
      return errors;
    }
    if (section === "permissions") {
      const roleId = data.role?.value || data.role || "";
      if (!String(roleId).trim()) errors.role = "Role is required.";
      return errors;
    }
    return getProfileValidationErrorMap(data);
  };

  const handleUserSaveUpdate = async (overrides = {}, options = {}) => {
    if (!isProfileUserActive) {
      showAlert({
        type: "warning",
        title: "User inactive",
        message: "This user is inactive. Profile changes are not allowed.",
      });
      return false;
    }
    const basicInfoOnly = Boolean(options?.basicInfoOnly);
    const section = options?.section || null;
    const quiet = Boolean(options?.quiet);
    const currentForm = {
      ...formData,
      ...(overrides &&
      typeof overrides === "object" &&
      !overrides.nativeEvent &&
      !overrides.target
        ? overrides
        : {}),
    };
    const nextForm = section
      ? buildSectionSaveForm(section, currentForm)
      : currentForm;

    const errorMap = basicInfoOnly
      ? (() => {
          const errors = {};
          const trim = (v) => String(v ?? "").trim();
          if (!trim(currentForm.firstName)) errors.firstName = "First name is required.";
          if (!trim(currentForm.lastName)) errors.lastName = "Last name is required.";
          return errors;
        })()
      : section
        ? getSectionValidationErrorMap(section, currentForm)
        : getProfileValidationErrorMap(nextForm);

    if (Object.keys(errorMap).length > 0) {
      setFieldErrors(errorMap);
      if (errorMap.studies) {
        showAlert({
          title: "Cannot update sharing",
          message: errorMap.studies,
          type: "warning",
        });
      } else if (basicInfoOnly) {
        showAlert({
          title: "Cannot update profile",
          message: Object.values(errorMap)[0] || "Please complete the required fields.",
          type: "warning",
        });
      }
      return false;
    }
    setFieldErrors({});

    const isCro = getIsCroForApi() === 1;
    if (!basicInfoOnly && !section && isExternalAdminView && isCro) {
      const shareError = externalScoping.getStudyShareValidationError();
      if (shareError) {
        setFieldErrors({ studies: shareError });
        return false;
      }
    }

    let documentsXml = "";
    let externalStaffIsfDocumentsPayload = [];
    if (
      !basicInfoOnly &&
      (!section || section === "studies") &&
      isExternalProfile &&
      isCro
    ) {
      try {
        [documentsXml, externalStaffIsfDocumentsPayload] = await Promise.all([
          externalScoping.buildCroDocumentListString(),
          externalScoping.buildExternalStaffIsfDocumentsPayload(),
        ]);
      } catch (e) {
        console.error("buildCroDocumentListString", e);
        showAlert({
          title: "Error",
          message: "Could not build document sharing payload. Try again.",
          type: "danger",
        });
        return false;
      }
    }

    try {
      const savingLabel =
        section === "studies"
          ? "Saving studies..."
          : section === "additional"
            ? "Saving additional info..."
            : section === "permissions"
              ? "Saving role..."
              : section === "address"
                ? "Saving address..."
                : "Saving user...";
      showLoader(savingLabel);

      const roleIdRef = getRoleIdValue(nextForm.role);
      const lineManagerId =
        nextForm.lineManager?.value ||
        (typeof nextForm.lineManager === "string" ? nextForm.lineManager : "") ||
        "";
      const departmentId =
        nextForm.department?.value ||
        (typeof nextForm.department === "string" ? nextForm.department : "") ||
        "";

      // Studies section: persist StudyList only (other fields stay on last saved snapshot).
      const studyListPayload = !isSystemAdministratorProfile
        ? {
            StudyList:
              nextForm.studies?.map((studyItem) => ({
                StudyId: studyItem.StudyId || studyItem.value,
                StudyName: studyItem.label || studyItem.StudyName || "",
              })) || [],
          }
        : {};

      const response = await api.post(API_ENDPOINTS.USER.SAVE_UPDATE_USER, {
        StaffId: staffId || "",
        FirstName: nextForm.firstName || "",
        LastName: nextForm.lastName || "",
        EmployeeNum: nextForm.employeeNumber,
        RoleIdRef: roleIdRef,
        Gender: nextForm.gender || "",
        StartDate: nextForm.startDate ? formatDate(nextForm.startDate) : "",
        AccessDate: nextForm.accessDate ? formatDate(nextForm.accessDate) : "",
        DOB: nextForm.dob ? formatDate(nextForm.dob) : "",
        EmailId: (nextForm.email || "").trim(),
        ContactNumber: (() => {
          const mobileCountryIso2 = String(
            nextForm.mobileCountryIso2 || orgCountryIso2 || "GB",
          )
            .trim()
            .toUpperCase();
          const result = validateNationalMobile(nextForm.contactNumber, {
            countryIso2: mobileCountryIso2,
            required: true,
          });
          return result.national || nextForm.contactNumber || "";
        })(),
        MobileCountryIso2: String(
          nextForm.mobileCountryIso2 || orgCountryIso2 || "GB",
        )
          .trim()
          .toUpperCase(),
        AddressLine1: nextForm.addressLine1 || "",
        City: nextForm.city || "",
        PostCode: nextForm.postalCode || "",
        County: nextForm.county || "",
        Country: nextForm.country || "",
        HaveLineManagerRights: nextForm.haveLineManagerRights ? 1 : 0,
        Action: "Update",
        LineManagerId: lineManagerId,
        DepartmentId: departmentId,
        ...studyListPayload,
        OrganizationId: userData?.OrganizationId || "",
        CreatedById: userData?.StaffId || "",
        LoggedInUserName:
          userData?.UserName ||
          userData?.FirstName + " " + userData?.LastName ||
          "",
        OrganizationName: userData?.OrganizationName || "",
        DocumentList: "",
        Base64Image: "",
        Extension: "",
        ProfilePictureUrl:
          nextForm.profilePicture?.startsWith?.("data:")
            ? ""
            : nextForm.profilePicture || profileData.profilePicture || "",
        IsCRO: getIsCroForApi(),
        // Keep sites / training roles from snapshot on studies-only save (do not apply dirty Profile edits).
        ...(!isExternalProfile &&
        getIsCroForApi() === 0 &&
        !isSystemAdministratorProfile
          ? {
              SiteList: (nextForm.selectedSites || []).map((site) => ({
                SiteId: site?.value || site?.SiteId || "",
                SiteName: site?.label || site?.SiteName || "",
              })),
            }
          : {}),
        ...(isExternalProfile &&
        isCro &&
        !basicInfoOnly &&
        (!section || section === "studies")
          ? {
              DocumentsXML: documentsXml,
              ExternalStaffISFDocuments: externalStaffIsfDocumentsPayload,
            }
          : {}),
        ...(orgHasQmsTrainingAdditionalRoles &&
        !isExternalProfile &&
        getIsCroForApi() === 0
          ? {
              AdditionalTrainingRoleIds: formatAdditionalTrainingRoleIdsForSave(
                nextForm.qmsTrainingAdditionalRoles,
              ),
            }
          : {}),
      });
      const storyRow = Array.isArray(response?.data)
        ? response.data[0]
        : response?.data?.SuccessStory?.[0] ||
          response?.data?.[0] ||
          null;
      const isSuccess =
        Number(response?.success) === 1 ||
        Number(storyRow?.Success) === 1 ||
        storyRow?.Success === true;
      if (isSuccess) {
        if (!quiet) {
          const defaultSuccessMessage =
            section === "studies"
              ? "Studies updated successfully."
              : section === "additional"
                ? "Additional information updated successfully."
                : section === "permissions"
                  ? "Role updated successfully."
                  : section === "address"
                    ? "Address updated successfully."
                    : "User updated successfully.";
          showAlert({
            title: "Success",
            message:
              section === "studies"
                ? defaultSuccessMessage
                : storyRow?.MSG ||
                  response?.message ||
                  defaultSuccessMessage,
            type: "success",
          });
        }
        if (section === "studies") {
          savedFormSnapshotRef.current = {
            ...(savedFormSnapshotRef.current || formData),
            studies: nextForm.studies || [],
          };
          setFormData((prev) => ({
            ...prev,
            studies: nextForm.studies || [],
          }));
          if (isExternalProfile && isCro) {
            try {
              const liveGrants = await fetchExternalEntityAccessGrants({
                organizationId: userData?.OrganizationId,
                staffId,
              });
              if (liveGrants != null) {
                setExternalEntityAccessFromApi(liveGrants);
              }
            } catch (grantErr) {
              console.error("refresh external entity access after save", grantErr);
            }
          }
        } else if (section) {
          savedFormSnapshotRef.current = nextForm;
        } else if (basicInfoOnly) {
          savedFormSnapshotRef.current = {
            ...(savedFormSnapshotRef.current || formData),
            ...nextForm,
          };
        } else {
          fetchUserProfile();
        }
        return true;
      }
      showAlert({
        title: "Error",
        message:
          storyRow?.MSG ||
          response?.message ||
          "Failed to update user.",
        type: "error",
      });
      return false;
    } catch (error) {
      console.error("Error updating user:", error);
      showAlert({
        title: "Error",
        message: "Failed to update user.",
        type: "error",
      });
      return false;
    } finally {
      hideLoader();
    }
  };

  const normalizePermissionSignature = (perms = []) =>
    filterAssignablePermissionKeys(perms)
      .map((key) => String(key).trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join("|");

  const getRoleDefaultPermissions = (role) => {
    const roleId = getRoleIdValue(role);
    const roleRecord = (roles || []).find(
      (item) =>
        String(item.value).toLowerCase() === String(roleId || "").toLowerCase(),
    );
    const roleLabel =
      (typeof role === "object" && role?.label) || roleRecord?.label || "";
    if (isSystemAdministratorRole(roleLabel)) {
      return filterAssignablePermissionKeys(getAllPermissionKeys());
    }
    return expandStoredIsfPermissionKeys(
      expandStoredQmsPermissionKeys(
        filterAssignablePermissionKeys(
          getEffectivePermissionKeys(roleLabel, roleRecord?.permission),
        ),
      ),
    );
  };

  const applyRoleChange = (role, { applyRolePermissions = false } = {}) => {
    const roleLabel =
      (typeof role === "object" && role?.label) ||
      String(role?.label || role || "");
    const isSysAdminRole = isSystemAdministratorRole(roleLabel);
    const nextRoleId = getRoleIdValue(role);
    const isOriginalRole =
      String(nextRoleId || "").toLowerCase() ===
      String(savedRoleIdRef.current || "").toLowerCase();

    setFormData((prev) => {
      const wasSysAdmin = isSystemAdministratorRole(prev.role);
      let nextStudies = prev.studies || [];
      if (isSysAdminRole && !wasSysAdmin) {
        studiesBeforeSysAdminRef.current = Array.isArray(prev.studies)
          ? [...prev.studies]
          : [];
        nextStudies = (studyList || []).map((opt) => ({
          StudyId: opt.value,
          label: opt.label,
          studyNumber: opt.studyNumber,
          studyName: opt.studyName,
          StudySchColor: opt.StudySchColor || opt.studyColor,
          studyColor: opt.studyColor || opt.StudySchColor,
        }));
      } else if (!isSysAdminRole && wasSysAdmin) {
        nextStudies =
          studiesBeforeSysAdminRef.current != null
            ? studiesBeforeSysAdminRef.current
            : prev.studies || [];
        studiesBeforeSysAdminRef.current = null;
      }
      return {
        ...prev,
        role: role || null,
        studies: nextStudies,
        qmsTrainingAdditionalRoles: (prev.qmsTrainingAdditionalRoles || []).filter(
          (item) =>
            String(item?.value ?? "").toLowerCase() !==
            nextRoleId.toLowerCase(),
        ),
      };
    });

    if (isOriginalRole) {
      setSelectedPermissions([...(savedPermissionsRef.current || [])]);
    } else if (applyRolePermissions) {
      setSelectedPermissions(getRoleDefaultPermissions(role));
    }
  };

  const handleRoles = (role) => {
    const nextRoleId = getRoleIdValue(role);
    const currentRoleId = getRoleIdValue(formData.role);
    if (
      String(nextRoleId || "").toLowerCase() ===
      String(currentRoleId || "").toLowerCase()
    ) {
      return;
    }

    const isOriginalRole =
      String(nextRoleId || "").toLowerCase() ===
      String(savedRoleIdRef.current || "").toLowerCase();

    if (isOriginalRole) {
      applyRoleChange(role, { applyRolePermissions: false });
      return;
    }

    setRoleChangeConfirm({ open: true, nextRole: role || null });
  };

  const closeRoleChangeConfirm = () =>
    setRoleChangeConfirm({ open: false, nextRole: null });

  const confirmRoleOnly = () => {
    const nextRole = roleChangeConfirm.nextRole;
    closeRoleChangeConfirm();
    if (nextRole) applyRoleChange(nextRole, { applyRolePermissions: false });
  };

  const confirmRoleWithPermissions = () => {
    const nextRole = roleChangeConfirm.nextRole;
    closeRoleChangeConfirm();
    if (nextRole) applyRoleChange(nextRole, { applyRolePermissions: true });
  };
  const handleQmsTrainingAdditionalRoles = (selected) => {
    if (!canEditAdminSections) return;
    const primaryId = getRoleIdValue(formData.role).toLowerCase();
    const next = (selected || []).filter(
      (item) => String(item?.value ?? "").toLowerCase() !== primaryId,
    );
    setFormData((prev) => ({
      ...prev,
      qmsTrainingAdditionalRoles: next,
    }));
  };
  const handleDepartment = (department) => {
    if (!canEditAdminSections) return;
    setFormData((prev) => ({ ...prev, department: department || null }));
  };
  const handleLineManager = (lineManager) =>
    setFormData((prev) => ({ ...prev, lineManager: lineManager || null }));
  const handleSites = (selected) => {
    if (!canEditAdminSections) return;
    setFormData((prev) => ({
      ...prev,
      selectedSites: Array.isArray(selected) ? selected : [],
    }));
  };
  const handlDOB = (date) => setFormData((prev) => ({ ...prev, dob: date }));
  const handlStartDate = (date) =>
    setFormData((prev) => ({ ...prev, startDate: date }));
  const handlAccessDate = (date) =>
    setFormData((prev) => ({ ...prev, accessDate: date }));
  const handleGenderChange = async (gender) => {
    const key = String(gender || "").trim().toLowerCase();
    const nextGender =
      key === "m" || key === "male"
        ? "M"
        : key === "f" || key === "female"
          ? "F"
          : key === "o" || key === "other"
            ? "O"
            : "";
    const previous = formData.gender || "";
    setFormData((prev) => ({ ...prev, gender: nextGender }));
    if (!canEditBasicInfo) return;
    const ok = await handleUserSaveUpdate(
      { gender: nextGender },
      { basicInfoOnly: true },
    );
    if (!ok) {
      setFormData((prev) => ({ ...prev, gender: previous }));
    }
  };

  const closeAllInlineEdits = () => {
    setNameEditOpen(false);
    setContactEditOpen(false);
    setGenderEditOpen(false);
    setDobEditOpen(false);
    setAddressEditOpen(false);
    setRoleEditOpen(false);
    setDepartmentEditOpen(false);
  };

  const openNameEditPopup = () => {
    const emp = String(formData.employeeNumber || "").trim();
    const parts = emp.split(/\s+/);
    const hasSplitPrefix = parts.length > 1;
    closeAllInlineEdits();
    setNameEditDraft({
      firstName: formData.firstName || "",
      lastName: formData.lastName || "",
      employeeNumber: hasSplitPrefix ? parts.slice(1).join(" ") : emp,
    });
    setNameEditOpen(true);
  };

  const applyNameEditPopup = async () => {
    const firstName = String(nameEditDraft.firstName || "").trim();
    const lastName = String(nameEditDraft.lastName || "").trim();
    setFormData((prev) => ({
      ...prev,
      firstName,
      lastName,
    }));
    setProfileData((prev) => ({
      ...prev,
      fullName: `${firstName} ${lastName}`.trim(),
    }));
    setNameEditOpen(false);
    if (canEditBasicInfo) {
      await handleUserSaveUpdate({ firstName, lastName }, { basicInfoOnly: true });
    }
  };

  const openContactEditPopup = () => {
    closeAllInlineEdits();
    setContactEditDraft({
      contactNumber: formData.contactNumber || "",
    });
    setContactEditOpen(true);
  };

  const openDobEditPopup = () => {
    closeAllInlineEdits();
    setDobEditDraft(formData.dob || null);
    setDobEditOpen(true);
  };

  const openAddressEditPopup = () => {
    closeAllInlineEdits();
    setAddressEditDraft({
      addressLine1: formData.addressLine1 || "",
      postalCode: formData.postalCode || "",
      city: formData.city || "",
      county: formData.county || "",
      country: formData.country || "",
    });
    setAddressEditOpen(true);
  };

  const applyAddressEditPopup = async () => {
    const nextAddress = {
      addressLine1: String(addressEditDraft.addressLine1 || "").trim(),
      postalCode: String(addressEditDraft.postalCode || "").trim(),
      city: String(addressEditDraft.city || "").trim(),
      county: String(addressEditDraft.county || "").trim(),
      country: String(addressEditDraft.country || "").trim(),
    };
    setFormData((prev) => ({ ...prev, ...nextAddress }));
    setAddressEditOpen(false);
    if (canEditBasicInfo || canEditAdminSections) {
      await handleUserSaveUpdate(nextAddress, { section: "address" });
    }
  };

  const applyDobEditPopup = async () => {
    const dob = dobEditDraft || null;
    setFormData((prev) => ({ ...prev, dob }));
    setDobEditOpen(false);
    if (canEditBasicInfo) {
      await handleUserSaveUpdate({ dob }, { basicInfoOnly: true });
    }
  };

  const applyContactEditPopup = async () => {
    const mobileCountryIso2 = String(
      formData.mobileCountryIso2 || orgCountryIso2 || "GB",
    )
      .trim()
      .toUpperCase();
    const result = validateNationalMobile(contactEditDraft.contactNumber, {
      countryIso2: mobileCountryIso2,
      required: true,
    });
    if (!result.valid) {
      showAlert({
        title: "Required fields",
        message: result.message,
        type: "warning",
      });
      return;
    }
    const contactNumber = result.national;
    setFormData((prev) => ({
      ...prev,
      contactNumber,
      mobileCountryIso2,
    }));
    setContactEditOpen(false);
    if (canEditBasicInfo) {
      await handleUserSaveUpdate(
        { contactNumber, mobileCountryIso2 },
        { basicInfoOnly: true },
      );
    }
  };

  const openRoleEditPopup = () => {
    closeAllInlineEdits();
    setRoleEditDraft(formData.role || null);
    setRoleEditOpen(true);
  };

  const applyRoleEditPopup = async () => {
    const role = roleEditDraft || null;
    if (!role) {
      showAlert({
        title: "Required fields",
        message: "Role is required.",
        type: "warning",
      });
      return;
    }
    applyRoleChange(role, { applyRolePermissions: false });
    setRoleEditOpen(false);
    if (canEditRole) {
      await handleUserSaveUpdate({ role }, { section: "permissions" });
    }
  };

  const openDepartmentEditPopup = () => {
    closeAllInlineEdits();
    setDepartmentEditDraft(formData.department || null);
    setDepartmentEditOpen(true);
  };

  const applyDepartmentEditPopup = async () => {
    const department = departmentEditDraft || null;
    setFormData((prev) => ({ ...prev, department }));
    setDepartmentEditOpen(false);
    if (canEditAdminSections || canEditRole) {
      await handleUserSaveUpdate({ department }, { section: "additional" });
    }
  };

  const minDate = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 5);

  // Extract file extension from base64 string
  const getFileExtensionFromBase64 = (base64String) => {
    if (!base64String) return "";
    const match = base64String.match(/data:image\/(\w+);base64,/);
    return match ? match[1] : "";
  };

  /** Convert typed signature text to image data URL (same format as draw) for saving. */
  const typedSignatureToDataUrl = (text) => {
    if (!text || !text.trim()) return null;
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 160;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#f8f9fa";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#1a1a1a";
    ctx.font = 'italic 42px "Dancing Script", "Brush Script MT", "Segoe Script", cursive';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text.trim(), canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL("image/png");
  };

  /** Separate API call to save e-signature to database (not the main profile API). Returns true on success. */
  const saveSignatureToBackend = async (signatureDataUrl) => {
    if (!isProfileUserActive) {
      showAlert({
        type: "warning",
        title: "User inactive",
        message: "This user is inactive. Signature cannot be updated.",
      });
      return false;
    }
    if (!signatureDataUrl || !staffId || !userData?.OrganizationId) return false;
    try {
      showLoader("Saving signature...");
      const response = await api.post(API_ENDPOINTS.USER.SAVE_ESIGNATURE, {
        StaffId: staffId,
        OrganizationId: userData.OrganizationId,
        SignatureBase64: signatureDataUrl,
        CreatedBy: userData.StaffId,
        Action:"UpdateSignature"
      });
      const success = response?.success === 1 || response?.data?.success === 1;
      if (success) {
        showAlert({
          title: "Success",
          message: "Signature saved successfully.",
          type: "success",
        });
        if (
          String(userData?.StaffId || "").toLowerCase() ===
          String(staffId || "").toLowerCase()
        ) {
          try {
            sessionStorage.setItem("SignatureBase64", signatureDataUrl);
          } catch (e) {
            console.error("Error storing signature in session:", e);
          }
        }
      }
      return !!success;
    } catch (error) {
      console.error("Error saving signature:", error);
      showAlert({
        title: "Error",
        message: "Failed to save signature. Please try again.",
        type: "error",
      });
      return false;
    } finally {
      hideLoader();
    }
  };

  // Auto-save profile picture when changed
  const handleProfilePictureChange = async (base64) => {
    // Update state immediately for UI feedback
    setFormData((prev) => ({
      ...prev,
      profilePicture: base64,
    }));
    setProfileData((prev) => ({
      ...prev,
      profilePicture: base64,
    }));

    // Auto-save to backend
    if (base64 && staffId) {
      try {
        showLoader("Updating profile picture...");

        const response = await api.post(API_ENDPOINTS.USER.SAVE_UPDATE_USER, {
          StaffId: staffId,
          FirstName: formData.firstName || "",
          LastName: formData.lastName || "",
          EmailId: (formData.email || "").trim(),
          ContactNumber: formData.contactNumber || "",
          Action: "UpdateProfilePicture",
          OrganizationId: userData?.OrganizationId || "",
          CreatedById: userData?.StaffId || "",
          LoggedInUserName:
            userData?.FirstName + " " + userData?.LastName ||
            userData?.Name ||
            "",
          OrganizationName: userData?.OrganizationName || "",
          Base64Image: base64,
          Extension: getFileExtensionFromBase64(base64),
        });

      } catch (error) {
        console.error("Error updating profile picture:", error);
      } finally {
        hideLoader();
      }
    }
  };

  const togglePermission = (key) => {
    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const selectManyPermissions = (keys = []) => {
    const normalized = Array.isArray(keys) ? keys : [];
    if (!normalized.length) return;
    setSelectedPermissions((prev) => Array.from(new Set([...prev, ...normalized])));
  };

  const deselectManyPermissions = (keys = []) => {
    const normalized = Array.isArray(keys) ? keys : [];
    if (!normalized.length) return;
    setSelectedPermissions((prev) => prev.filter((p) => !normalized.includes(p)));
  };

  const handleSavePermissions = async ({ quiet = false } = {}) => {
    try {
      showLoader("Saving permissions");

      const response = await api.post(API_ENDPOINTS.USER.GET_INDIVIDUAL_USER, {
        OrganizationId: userData.OrganizationId,
        StaffIDRef: userData.StaffId,
        CreatedBy: userData.StaffId,
        StaffId: staffId,
        Action: "UpdateuserPermission",
        Permission: sanitizeSelectedPermissionsForSave(selectedPermissions).join(","),
      });
      if (response?.success === 1) {
        if (!quiet) {
          showAlert({
            title: "Success",
            message: response.data?.[0]?.MSG || "Permissions updated successfully.",
            type: "success",
          });
        }
        return true;
      }
      if (!quiet) {
        showAlert({
          title: "Error",
          message: response?.data?.[0]?.MSG || "Failed to update permissions.",
          type: "error",
        });
      }
      return false;
    } catch (error) {
      console.error("Error saving permissions", error);
      if (!quiet) {
        showAlert({
          title: "Error",
          message: "Failed to update permissions.",
          type: "error",
        });
      }
      return false;
    } finally {
      hideLoader();
    }
  };

  const handleSaveAdditionalSection = () =>
    handleUserSaveUpdate({}, { section: "additional" });

  const handleSaveStudiesSection = (formOverrides = {}) => {
    if (isSystemAdministratorProfile) {
      showAlert({
        title: "Info",
        message:
          "System Administrator has access to all studies — assignment is automatic.",
        type: "info",
      });
      return false;
    }
    const overrides =
      formOverrides &&
      typeof formOverrides === "object" &&
      !formOverrides.nativeEvent &&
      !formOverrides.target
        ? formOverrides
        : {};
    // Section save: StudyList only (other user fields taken from last saved snapshot).
    return handleUserSaveUpdate(overrides, { section: "studies" });
  };

  const permissionsHaveUnsavedChanges = () =>
    normalizePermissionSignature(selectedPermissions) !==
    normalizePermissionSignature(savedPermissionsRef.current);

  const persistRolePermissionsSection = async () => {
    const roleSaved = await handleUserSaveUpdate(
      {},
      { section: "permissions", quiet: true },
    );
    if (!roleSaved) return false;
    if (!isExternalProfile && canEditPermissionCards) {
      const permissionsSaved = await handleSavePermissions({ quiet: true });
      if (!permissionsSaved) {
        showAlert({
          title: "Error",
          message: "Role saved, but permissions failed to update.",
          type: "error",
        });
        return false;
      }
    }
    savedPermissionsRef.current = filterAssignablePermissionKeys(
      selectedPermissions || [],
    );
    savedRoleIdRef.current = getRoleIdValue(formData.role);
    showAlert({
      title: "Success",
      message: "Role and permissions updated successfully.",
      type: "success",
    });
    return true;
  };

  const handleSaveRolePermissionsSection = async () => {
    if (permissionsHaveUnsavedChanges()) {
      setPermissionSaveConfirmOpen(true);
      return false;
    }
    return persistRolePermissionsSection();
  };

  const confirmPermissionSave = async () => {
    setPermissionSaveConfirmOpen(false);
    return persistRolePermissionsSection();
  };

  const handleProfileInlineSave = async () => {
    await handleUserSaveUpdate();
    if (!isExternalProfile && canEditPermissionCards) {
      await handleSavePermissions();
    }
  };

  const profileActionUser = useMemo(
    () => ({
      StaffId: staffId,
      Name:
        profileData.fullName ||
        [formData.firstName, formData.lastName].filter(Boolean).join(" ") ||
        "this user",
      EmailId: formData.email || "",
      IsCRO: formData.IsCRO,
      IsCro: formData.IsCRO,
      Status: isProfileUserActive ? "Active" : "Inactive",
    }),
    [
      staffId,
      profileData.fullName,
      formData.firstName,
      formData.lastName,
      formData.email,
      formData.IsCRO,
      isProfileUserActive,
    ],
  );

  const handleAdminCredAction = useCallback(() => {
    if (!isProfileUserActive || !canChangeCredentials) return;
    setAdminCredForm({ open: true, row: profileActionUser });
  }, [isProfileUserActive, canChangeCredentials, profileActionUser]);

  const handleAdminCredFormClose = () => {
    setAdminCredForm({ open: false, row: null });
  };

  const handleAdminCredFormSubmit = ({ mode, value }) => {
    const { row } = adminCredForm;
    handleAdminCredFormClose();
    setAdminCredConfirm({ open: true, mode, row, value });
  };

  const handleAdminCredConfirmClose = () => {
    setAdminCredConfirm({ open: false, mode: null, row: null, value: "" });
  };

  const handleAdminCredConfirmYes = () => {
    const { mode, row, value } = adminCredConfirm;
    setPendingAdminCred({ mode, row, value });
    handleAdminCredConfirmClose();
    setAdminCredAuthOpen(true);
  };

  const handleAdminCredAuthClose = () => {
    setAdminCredAuthOpen(false);
    setPendingAdminCred(null);
  };

  const handleAdminCredAuthSubmit = async () => {
    if (!pendingAdminCred?.row || !userData?.OrganizationId || !userData?.StaffId) {
      return;
    }

    const { mode, row, value } = pendingAdminCred;
    const basePayload = {
      OrganizationId: userData.OrganizationId,
      StaffId: row.StaffId,
      CreatedBy: userData.StaffId,
    };

    try {
      showLoader(
        mode === "password" ? "Changing password..." : "Changing access code...",
      );
      const response =
        mode === "password"
          ? await adminChangeUserPassword({ ...basePayload, Password: value })
          : await adminChangeUserAccessCode({ ...basePayload, AccessCode: value });
      const parsed = parseAdminCredentialChangeResponse(response);

      setPendingAdminCred(null);
      setAdminCredAuthOpen(false);

      if (parsed.ok) {
        showAlert({
          title: "Success",
          message: parsed.message,
          type: "success",
        });
      } else {
        showAlert({
          title: "Error",
          message: parsed.message,
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Could not update credentials.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const loadMfaEnrollment = useCallback(async () => {
    if (!staffId || !canResetMfa) {
      setHasAuthenticatorEnrolled(false);
      return;
    }
    try {
      const response = await getMfaSettings(staffId);
      setHasAuthenticatorEnrolled(isAuthenticatorEnrolled(response));
    } catch {
      setHasAuthenticatorEnrolled(false);
    }
  }, [staffId, canResetMfa]);

  useEffect(() => {
    setHasAuthenticatorEnrolled(false);
    if (staffId && canResetMfa) {
      loadMfaEnrollment();
    }
  }, [staffId, canResetMfa, loadMfaEnrollment]);

  const handleMfaResetClick = useCallback(async () => {
    if (!isProfileUserActive || !canResetMfa || !staffId) return;
    try {
      showLoader("Checking authenticator...");
      const response = await getMfaSettings(staffId);
      const enrolled = isAuthenticatorEnrolled(response);
      setHasAuthenticatorEnrolled(enrolled);
      if (!enrolled) {
        showAlert({
          title: "No authenticator",
          message:
            "This user has not registered an MFA authenticator app. Reset is only available after authenticator setup.",
          type: "warning",
        });
        return;
      }
      setMfaResetConfirmOpen(true);
    } catch (err) {
      showAlert({
        title: "Unable to check MFA",
        message: err?.message || "Could not verify authenticator status.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  }, [
    isProfileUserActive,
    canResetMfa,
    staffId,
    showLoader,
    hideLoader,
    showAlert,
  ]);

  const handleMfaResetConfirmYes = () => {
    setMfaResetConfirmOpen(false);
    setMfaResetAuthOpen(true);
  };

  const handleMfaResetAuthClose = () => {
    setMfaResetAuthOpen(false);
  };

  const handleMfaResetAuthSubmit = async () => {
    if (!staffId || !userData?.OrganizationId || !userData?.StaffId) return;
    try {
      showLoader("Resetting authenticator...");
      const response = await resetAuthenticator({
        organizationId: userData.OrganizationId,
        staffId,
        createdBy: userData.StaffId,
      });
      setMfaResetAuthOpen(false);
      if (Number(response?.success) === 1) {
        setHasAuthenticatorEnrolled(false);
        showAlert({
          title: "Authenticator reset",
          message:
            response?.message ||
            "Authenticator cleared. The user can sign in with email OTP and register a new authenticator app.",
          type: "success",
        });
      } else {
        showAlert({
          title: "Reset failed",
          message: response?.message || "Could not reset authenticator.",
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Reset failed",
        message: err?.message || "Could not reset authenticator.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const closeDocAccessModal = useCallback(() => {
    setDocAccessModal({
      open: false,
      mode: "library",
      row: null,
    });
  }, []);

  const openStaffDocumentAccess = useCallback(
    async (mode) => {
      if (!isProfileUserActive || !staffId || !userData?.OrganizationId) return;
      if (
        (mode === "library" && !canManageLibraryStaffAccess) ||
        (mode === "myLearning" && !canManageMyLearningStaffAccess)
      ) {
        return;
      }
      if (isSponsorCroUserRecord(formData.IsCRO) || isExternalProfile) {
        showAlert({
          title: "Not available",
          message: "Document access assignment is available for internal users only.",
          type: "warning",
        });
        return;
      }

      const fetchFn =
        mode === "library"
          ? fetchStaffDocumentLibraryDocuments
          : fetchStaffMyLearningDocuments;
      try {
        showLoader(
          mode === "library"
            ? "Checking document library access..."
            : "Loading My Learning access...",
        );
        const response = await fetchFn(userData, staffId, {
          pageNumber: 1,
          pageSize: 1,
        });
        const story = firstSuccessStoryRow(response);

        if (!isApiSuccess(response, story)) {
          showAlert({
            title:
              mode === "library"
                ? "Document Library Access"
                : "My Learning Access",
            message:
              apiMessage(response, story) ||
              (mode === "library"
                ? "Assign this user Document Library permission first."
                : "Could not load My Learning documents."),
            type: "warning",
          });
          return;
        }

        setDocAccessModal({
          open: true,
          mode,
          row: profileActionUser,
        });
      } catch (error) {
        showAlert({
          title: "Error",
          message: error?.message || "Failed to load document access.",
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [
      isProfileUserActive,
      staffId,
      userData,
      canManageLibraryStaffAccess,
      canManageMyLearningStaffAccess,
      formData.IsCRO,
      isExternalProfile,
      profileActionUser,
      showLoader,
      hideLoader,
      showAlert,
    ],
  );

  const isInternalProfileTarget =
    !isExternalProfile && !isSponsorCroUserRecord(formData.IsCRO);

  const breadcrumbActions = [
    {
      icon: "fas fa-refresh",
      label: "Refresh",
      onClick: () => fetchUserProfile(),
    },
    ...(isProfileUserActive && canChangeCredentials
      ? [
          {
            icon: "fas fa-key",
            label: "Change credentials",
            onClick: handleAdminCredAction,
          },
        ]
      : []),
    ...(isProfileUserActive && canResetMfa && hasAuthenticatorEnrolled
      ? [
          {
            icon: "fas fa-mobile-alt",
            label: "Reset MFA authenticator",
            onClick: handleMfaResetClick,
          },
        ]
      : []),
    ...(isProfileUserActive &&
    isInternalProfileTarget &&
    canManageLibraryStaffAccess
      ? [
          {
            icon: "fas fa-folder-open",
            label: "Document Library Access",
            onClick: () => openStaffDocumentAccess("library"),
          },
        ]
      : []),
    ...(isProfileUserActive &&
    isInternalProfileTarget &&
    canManageMyLearningStaffAccess
      ? [
          {
            icon: "fas fa-graduation-cap",
            label: "My Learning Access",
            onClick: () => openStaffDocumentAccess("myLearning"),
          },
        ]
      : []),
  ];

  return (
    <div
      className={`user-profile-page${
        embedded ? " user-profile-page--embedded" : ""
      }`}
    >
      {embedded ? (
        !isProfileUserActive ? (
          <div
            className="alert alert-warning mb-2 py-1 px-2 profile-inactive-toolbar-alert"
            role="status"
          >
            <i className="fas fa-lock me-1" aria-hidden="true" />
            This user is inactive. Profile is view only.
          </div>
        ) : null
      ) : (
        <PageBreadcrumb
          items={breadcrumbItems}
          actions={breadcrumbActions}
          toolbarExtras={
            !isProfileUserActive ? (
              <div
                className="alert alert-warning mb-0 py-1 px-2 profile-inactive-toolbar-alert"
                role="status"
              >
                <i className="fas fa-lock me-1" aria-hidden="true" />
                This user is inactive. Profile is view only.
              </div>
            ) : null
          }
        />
      )}
      <main className="user-profile-main">
        <div className="user-profile-container">
          {/* E-Signature modal (popup) */}
          {showSignatureModal && (
            <div
              className="modal fade show d-block profile-signature-modal-overlay"
              style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label="Draw your signature"
              onClick={(e) => e.target === e.currentTarget && setShowSignatureModal(false)}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-pen-fancy me-2" aria-hidden="true" />
                      E-Signature
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowSignatureModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="profile-signature-mode-tabs mb-3">
                      <button
                        type="button"
                        className={`btn btn-sm ${signatureMode === "draw" ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setSignatureMode("draw")}
                      >
                        <i className="fas fa-pen me-1" aria-hidden="true" />
                        Draw
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${signatureMode === "type" ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setSignatureMode("type")}
                      >
                        <i className="fas fa-keyboard me-1" aria-hidden="true" />
                        Type
                      </button>
                    </div>
                    {signatureMode === "draw" ? (
                      <>
                        <p className="text-muted small mb-2">Draw your signature in the box below.</p>
                        <div className="profile-signature-canvas-wrap">
                          <SignatureCanvas
                            ref={signatureCanvasRef}
                            canvasProps={{
                              className: "profile-signature-canvas",
                              width: 400,
                              height: 160,
                            }}
                            penColor="#1a1a1a"
                            backgroundColor="#f8f9fa"
                            clearOnResize={false}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-muted small mb-2">Type your signature in the box below.</p>
                        <input
                          type="text"
                          className="form-control profile-signature-type-input"
                          placeholder="e.g. John Smith"
                          value={typedSignature}
                          onChange={(e) => setTypedSignature(e.target.value)}
                          aria-label="Type your signature"
                        />
                        {typedSignature.trim() && (
                          <div className="profile-signature-type-preview mt-2">
                            <span className="profile-signature-type-preview-text">{typedSignature.trim()}</span>
                          </div>
                        )}
                      </>
                    )}
                    <div className="profile-signature-actions mt-3">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          if (signatureMode === "draw") signatureCanvasRef.current?.clear();
                          else setTypedSignature("");
                        }}
                        aria-label="Clear"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={async () => {
                          let dataUrl = null;
                          if (signatureMode === "draw") {
                            if (signatureCanvasRef.current?.isEmpty()) return;
                            dataUrl = signatureCanvasRef.current.toDataURL("image/png");
                          } else {
                            dataUrl = typedSignatureToDataUrl(typedSignature);
                            if (!dataUrl) return;
                          }
                          const saved = await saveSignatureToBackend(dataUrl);
                          if (saved) {
                            setESignatureDataUrl(dataUrl);
                            setShowSignatureModal(false);
                            setTypedSignature("");
                          }
                        }}
                        aria-label="Save signature"
                      >
                        Save signature
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowSignatureModal(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Common identity sidebar + Tabs */}
          <div className="profile-main profile-main--with-sidebar">
            <aside className="profile-sidebar" aria-label="User basic information">
                    <div className="profile-sidebar-content">
                      <div className="profile-image-wrapper">
                        <div className="profile-image-wrapper__photo">
                          <SimpleCircularUpload
                            defaultImage={null}
                            currentImage={
                              formData.profilePicture ||
                              profileData.profilePicture ||
                              null
                            }
                            size={72}
                            maxFileSize={5}
                            disabled={!canEditBasicInfo}
                            onChange={handleProfilePictureChange}
                          />
                        </div>
                        <div className="profile-image-wrapper__meta">
                          <div className="profile-meta-chip-row">
                            <span
                              className="profile-emp-badge"
                              title={`Employee #: ${formData.employeeNumber || "N/A"}`}
                            >
                              {formData.employeeNumber || "N/A"}
                            </span>
                            <span
                              className={`badge profile-user-type-badge ${
                                getIsCroForApi() === 1 ? "bg-danger" : "bg-success"
                              }`}
                            >
                              {formatUserTypeLabel(getIsCroForApi())}
                            </span>
                          </div>
                          {userData?.OrganizationName ? (
                            <div
                              className="profile-org-under-type"
                              title={userData.OrganizationName}
                            >
                              <i className="fas fa-building" aria-hidden="true" />
                              <span>{userData.OrganizationName}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="profile-info">
                        <div className="profile-name-block profile-name-block--with-edit profile-name-block--info">
                          <div className="profile-name-stack">
                            <span
                              className={`badge profile-account-status-badge ${
                                isProfileUserActive ? "is-active" : "is-inactive"
                              }`}
                              title={
                                isProfileUserActive
                                  ? "Account is active"
                                  : "Account is inactive — profile is view only"
                              }
                            >
                              {isProfileUserActive ? "Active" : "Inactive"}
                            </span>
                            <div className="profile-name-row">
                            <i className="fas fa-user" aria-hidden="true" />
                            <h3
                              className="profile-name"
                              title={
                                profileData.fullName ||
                                [formData.firstName, formData.lastName]
                                  .filter(Boolean)
                                  .join(" ") ||
                                "Loading..."
                              }
                            >
                              {profileData.fullName ||
                                [formData.firstName, formData.lastName]
                                  .filter(Boolean)
                                  .join(" ") ||
                                "Loading..."}
                            </h3>
                            {canEditBasicInfo ? (
                              <div className="profile-inline-edit">
                                <button
                                  type="button"
                                  ref={nameEditAnchorRef}
                                  className="profile-inline-edit__btn"
                                  aria-label="Edit name"
                                  title="Edit name"
                                  onClick={() =>
                                    nameEditOpen
                                      ? setNameEditOpen(false)
                                      : openNameEditPopup()
                                  }
                                >
                                  <i className="fas fa-pen" aria-hidden="true" />
                                </button>
                                <ProfileInlinePopover
                                  open={nameEditOpen}
                                  anchorRef={nameEditAnchorRef}
                                  aria-label="Edit name"
                                >
                                  <div className="profile-inline-edit__popover-title">
                                    Edit name
                                  </div>
                                  <label className="profile-inline-edit__label">
                                    First name
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={nameEditDraft.firstName}
                                      onChange={(e) =>
                                        setNameEditDraft((prev) => ({
                                          ...prev,
                                          firstName: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                  <label className="profile-inline-edit__label">
                                    Last name
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={nameEditDraft.lastName}
                                      onChange={(e) =>
                                        setNameEditDraft((prev) => ({
                                          ...prev,
                                          lastName: e.target.value,
                                        }))
                                      }
                                    />
                                  </label>
                                  <label className="profile-inline-edit__label">
                                    Employee #
                                    <div className="profile-inline-edit__emp profile-inline-edit__emp--readonly">
                                      <span>
                                        {profileEditFormData.prefix || "EMP"}
                                      </span>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={nameEditDraft.employeeNumber}
                                        readOnly
                                        disabled
                                        tabIndex={-1}
                                        aria-readonly="true"
                                      />
                                    </div>
                                  </label>
                                  <div className="profile-inline-edit__actions">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setNameEditOpen(false)}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={applyNameEditPopup}
                                    >
                                      Save
                                    </button>
                                  </div>
                                </ProfileInlinePopover>
                              </div>
                            ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="profile-role-department profile-info-row--with-edit">
                          <i className="fas fa-user-tag" aria-hidden="true" />
                          <span className="profile-info-value-wrap">
                            <span className="profile-info-value">
                              {isExternalProfile ? "Role: " : ""}
                              {formData.role?.label || profileData.role || "N/A"}
                            </span>
                            {isExternalProfile && canEditRole ? (
                              <div className="profile-inline-edit">
                                <button
                                  type="button"
                                  ref={roleEditAnchorRef}
                                  className="profile-inline-edit__btn"
                                  aria-label="Edit role"
                                  title="Edit role"
                                  onClick={() =>
                                    roleEditOpen
                                      ? setRoleEditOpen(false)
                                      : openRoleEditPopup()
                                  }
                                >
                                  <i className="fas fa-pen" aria-hidden="true" />
                                </button>
                                <ProfileInlinePopover
                                  open={roleEditOpen}
                                  anchorRef={roleEditAnchorRef}
                                  aria-label="Edit role"
                                  width={240}
                                >
                                  <div className="profile-inline-edit__popover-title">
                                    Edit role
                                  </div>
                                  <label className="profile-inline-edit__label">
                                    Role
                                    <Dropdown
                                      options={visibleRoles}
                                      isMulti={false}
                                      selected={roleEditDraft}
                                      onSelect={setRoleEditDraft}
                                    />
                                  </label>
                                  <div className="profile-inline-edit__actions">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setRoleEditOpen(false)}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={applyRoleEditPopup}
                                    >
                                      Save
                                    </button>
                                  </div>
                                </ProfileInlinePopover>
                              </div>
                            ) : null}
                          </span>
                        </div>
                        {isExternalProfile ? (
                          <div className="profile-department profile-info-row--with-edit">
                            <i className="fas fa-sitemap" aria-hidden="true" />
                            <span className="profile-info-value-wrap">
                              <span className="profile-info-value">
                                Department: {formData.department?.label || "N/A"}
                              </span>
                              {canEditAdminSections || canEditRole ? (
                                <div className="profile-inline-edit">
                                  <button
                                    type="button"
                                    ref={departmentEditAnchorRef}
                                    className="profile-inline-edit__btn"
                                    aria-label="Edit department"
                                    title="Edit department"
                                    onClick={() =>
                                      departmentEditOpen
                                        ? setDepartmentEditOpen(false)
                                        : openDepartmentEditPopup()
                                    }
                                  >
                                    <i className="fas fa-pen" aria-hidden="true" />
                                  </button>
                                  <ProfileInlinePopover
                                    open={departmentEditOpen}
                                    anchorRef={departmentEditAnchorRef}
                                    aria-label="Edit department"
                                    width={240}
                                    estimatedHeight={280}
                                  >
                                    <div className="profile-inline-edit__popover-title">
                                      Edit department
                                    </div>
                                    <label className="profile-inline-edit__label">
                                      Department
                                      <Dropdown
                                        options={sponsorCroDepartments}
                                        isMulti={false}
                                        selected={departmentEditDraft}
                                        onSelect={setDepartmentEditDraft}
                                      />
                                    </label>
                                    <div className="profile-inline-edit__actions">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() =>
                                          setDepartmentEditOpen(false)
                                        }
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={applyDepartmentEditPopup}
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </ProfileInlinePopover>
                                </div>
                              ) : null}
                            </span>
                          </div>
                        ) : null}
                        <div className="profile-contact profile-info-row--with-edit">
                          <i className="fas fa-phone" aria-hidden="true" />
                          <span className="profile-info-value-wrap">
                            <span className="profile-info-value">
                              {formData.contactNumber || "N/A"}
                            </span>
                            {canEditBasicInfo ? (
                              <div className="profile-inline-edit">
                                <button
                                  type="button"
                                  ref={contactEditAnchorRef}
                                  className="profile-inline-edit__btn"
                                  aria-label="Edit contact"
                                  title="Edit contact"
                                  onClick={() =>
                                    contactEditOpen
                                      ? setContactEditOpen(false)
                                      : openContactEditPopup()
                                  }
                                >
                                  <i className="fas fa-pen" aria-hidden="true" />
                                </button>
                                <ProfileInlinePopover
                                  open={contactEditOpen}
                                  anchorRef={contactEditAnchorRef}
                                  aria-label="Edit contact"
                                >
                                  <div className="profile-inline-edit__popover-title">
                                    Edit contact
                                  </div>
                                  <label className="profile-inline-edit__label">
                                    Contact number
                                    <div className="um-phone-input">
                                      <select
                                        className="form-control form-control-sm um-phone-input__dial"
                                        value={orgMobileDialLabel(
                                          formData.mobileCountryIso2 ||
                                            orgCountryIso2 ||
                                            "GB",
                                        )}
                                        disabled
                                        aria-label="Organisation country dial code"
                                      >
                                        <option
                                          value={orgMobileDialLabel(
                                            formData.mobileCountryIso2 ||
                                              orgCountryIso2 ||
                                              "GB",
                                          )}
                                        >
                                          {orgMobileDialLabel(
                                            formData.mobileCountryIso2 ||
                                              orgCountryIso2 ||
                                              "GB",
                                          )}
                                        </option>
                                      </select>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={contactEditDraft.contactNumber}
                                        onChange={(e) => {
                                          const iso =
                                            formData.mobileCountryIso2 ||
                                            orgCountryIso2 ||
                                            "GB";
                                          const { national } =
                                            normalizeNationalMobileInput(
                                              e.target.value,
                                              iso,
                                            );
                                          setContactEditDraft((prev) => ({
                                            ...prev,
                                            contactNumber: national,
                                          }));
                                        }}
                                        onPaste={(e) => {
                                          const text =
                                            e.clipboardData?.getData("text") ??
                                            "";
                                          e.preventDefault();
                                          const iso =
                                            formData.mobileCountryIso2 ||
                                            orgCountryIso2 ||
                                            "GB";
                                          const { national } =
                                            normalizeNationalMobileInput(
                                              text,
                                              iso,
                                            );
                                          setContactEditDraft((prev) => ({
                                            ...prev,
                                            contactNumber: national,
                                          }));
                                        }}
                                        inputMode="numeric"
                                        placeholder="7123456789"
                                      />
                                    </div>
                                  </label>
                                  <div className="profile-inline-edit__actions">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setContactEditOpen(false)}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={applyContactEditPopup}
                                    >
                                      Save
                                    </button>
                                  </div>
                                </ProfileInlinePopover>
                              </div>
                            ) : null}
                          </span>
                        </div>
                        <div className="profile-email">
                          <i className="fas fa-envelope" aria-hidden="true" />
                          <span>{formData.email || "N/A"}</span>
                        </div>
                        <div className="profile-dob profile-info-row--with-edit">
                          <i className="fas fa-birthday-cake" aria-hidden="true" />
                          <span className="profile-info-value-wrap">
                            <span className="profile-info-value">
                              DOB:{" "}
                              {formData.dob ? formatDate(formData.dob) : "N/A"}
                            </span>
                            {canEditBasicInfo ? (
                              <div className="profile-inline-edit">
                                <button
                                  type="button"
                                  ref={dobEditAnchorRef}
                                  className="profile-inline-edit__btn"
                                  aria-label="Edit date of birth"
                                  title="Edit date of birth"
                                  onClick={() =>
                                    dobEditOpen
                                      ? setDobEditOpen(false)
                                      : openDobEditPopup()
                                  }
                                >
                                  <i className="fas fa-pen" aria-hidden="true" />
                                </button>
                                <ProfileInlinePopover
                                  open={dobEditOpen}
                                  anchorRef={dobEditAnchorRef}
                                  aria-label="Edit date of birth"
                                >
                                  <div className="profile-inline-edit__popover-title">
                                    Edit date of birth
                                  </div>
                                  <label className="profile-inline-edit__label">
                                    DOB
                                    <DatePicker
                                      selected={dobEditDraft}
                                      onChange={(date) => setDobEditDraft(date)}
                                      placeholderText="Select date"
                                      className="form-control form-control-sm"
                                      dateFormat="dd/MM/yyyy"
                                      isClearable
                                      showMonthDropdown
                                      showYearDropdown
                                      dropdownMode="select"
                                      maxDate={new Date()}
                                      onChangeRaw={(e) => e.preventDefault()}
                                      popperProps={{ strategy: "fixed" }}
                                    />
                                  </label>
                                  <div className="profile-inline-edit__actions">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setDobEditOpen(false)}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={applyDobEditPopup}
                                    >
                                      Save
                                    </button>
                                  </div>
                                </ProfileInlinePopover>
                              </div>
                            ) : null}
                          </span>
                        </div>
                        <div className="profile-gender">
                          <i className="fas fa-venus-mars" aria-hidden="true" />
                          <div className="profile-gender-edit-row">
                            {genderEditOpen && canEditBasicInfo ? (
                              <GenderSelector
                                value={formData.gender}
                                onChange={(value) => {
                                  handleGenderChange(value);
                                  setGenderEditOpen(false);
                                }}
                                size="small"
                              />
                            ) : (
                              <>
                                <span className="profile-gender-edit-value">
                                  {(() => {
                                    const key = String(formData.gender || "")
                                      .trim()
                                      .toLowerCase();
                                    if (key === "m" || key === "male")
                                      return "Male";
                                    if (key === "f" || key === "female")
                                      return "Female";
                                    if (key === "o" || key === "other")
                                      return "Other";
                                    return formData.gender
                                      ? String(formData.gender)
                                      : "Not set";
                                  })()}
                                </span>
                                {canEditBasicInfo ? (
                                  <button
                                    type="button"
                                    className="profile-inline-edit__btn"
                                    aria-label="Edit gender"
                                    title="Edit gender"
                                    onClick={() => {
                                      closeAllInlineEdits();
                                      setGenderEditOpen(true);
                                    }}
                                  >
                                    <i className="fas fa-pen" aria-hidden="true" />
                                  </button>
                                ) : null}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="profile-address profile-info-row--with-edit">
                          <i className="fas fa-map-marker-alt" aria-hidden="true" />
                          <span className="profile-info-value-wrap">
                            <span
                              className="profile-info-value"
                              title={formatAddressSummary()}
                            >
                              {formatAddressSummary()}
                            </span>
                            {canEditBasicInfo || canEditAdminSections ? (
                              <div className="profile-inline-edit">
                                <button
                                  type="button"
                                  ref={addressEditAnchorRef}
                                  className="profile-inline-edit__btn"
                                  aria-label="Edit address"
                                  title="Edit address"
                                  onClick={() =>
                                    addressEditOpen
                                      ? setAddressEditOpen(false)
                                      : openAddressEditPopup()
                                  }
                                >
                                  <i className="fas fa-pen" aria-hidden="true" />
                                </button>
                                <ProfileInlinePopover
                                  open={addressEditOpen}
                                  anchorRef={addressEditAnchorRef}
                                  aria-label="Edit address"
                                  width={280}
                                  estimatedHeight={360}
                                  placement="right"
                                  className="profile-inline-edit__popover--address"
                                >
                                  <div className="profile-inline-edit__popover-title">
                                    Edit address
                                  </div>
                                  <div className="profile-address-edit-form">
                                    <label className="profile-inline-edit__label">
                                      Address
                                      <textarea
                                        className="form-control form-control-sm"
                                        rows={2}
                                        value={addressEditDraft.addressLine1}
                                        onChange={(e) =>
                                          setAddressEditDraft((prev) => ({
                                            ...prev,
                                            addressLine1: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                    <label className="profile-inline-edit__label">
                                      Postal Code
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={addressEditDraft.postalCode}
                                        onChange={(e) =>
                                          setAddressEditDraft((prev) => ({
                                            ...prev,
                                            postalCode: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                    <label className="profile-inline-edit__label">
                                      City
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={addressEditDraft.city}
                                        onChange={(e) =>
                                          setAddressEditDraft((prev) => ({
                                            ...prev,
                                            city: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                    <label className="profile-inline-edit__label">
                                      County
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={addressEditDraft.county}
                                        onChange={(e) =>
                                          setAddressEditDraft((prev) => ({
                                            ...prev,
                                            county: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                    <label className="profile-inline-edit__label">
                                      Country
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={addressEditDraft.country}
                                        onChange={(e) =>
                                          setAddressEditDraft((prev) => ({
                                            ...prev,
                                            country: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                  </div>
                                  <div className="profile-inline-edit__actions">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setAddressEditOpen(false)}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={applyAddressEditPopup}
                                    >
                                      Save
                                    </button>
                                  </div>
                                </ProfileInlinePopover>
                              </div>
                            ) : null}
                          </span>
                        </div>
                        <div className="profile-start-date">
                          <i className="fas fa-calendar-plus" aria-hidden="true" />
                          <span>
                            Start date:{" "}
                            {formData.startDate
                              ? formatDate(formData.startDate)
                              : "N/A"}
                          </span>
                        </div>
                        <div className="profile-access-date">
                          <i className="fas fa-calendar-check" aria-hidden="true" />
                          <span>
                            Access date:{" "}
                            {formData.accessDate
                              ? formatDate(formData.accessDate)
                              : "N/A"}
                          </span>
                        </div>
                        <div className="profile-last-login">
                          <i className="fas fa-clock" aria-hidden="true" />
                          <span>
                            Last login:{" "}
                            {profileData.lastLogin &&
                            profileData.lastLogin !== "—"
                              ? profileData.lastLogin
                              : "N/A"}
                          </span>
                        </div>
                        <div className="profile-signature-row">
                          <i className="fas fa-pen-fancy" aria-hidden="true" />
                          {eSignatureDataUrl ? (
                            <span className="profile-signature-block">
                              <img
                                src={eSignatureDataUrl}
                                alt="Signature"
                                className="profile-signature-thumb"
                              />
                              {canEditSignature ? (
                                <button
                                  type="button"
                                  className="btn-link profile-signature-change"
                                  onClick={() => {
                                    setShowSignatureModal(true);
                                    setTimeout(
                                      () => signatureCanvasRef.current?.clear(),
                                      100,
                                    );
                                  }}
                                  aria-label="Change signature"
                                >
                                  Change
                                </button>
                              ) : null}
                            </span>
                          ) : canEditSignature ? (
                            <button
                              type="button"
                              className="btn-link profile-signature-add"
                              onClick={() => setShowSignatureModal(true)}
                              aria-label="Add signature"
                            >
                              Add signature
                            </button>
                          ) : (
                            <span className="text-muted">No signature on file</span>
                          )}
                        </div>
                      </div>
                    </div>
              </aside>
            <div className="profile-tab-shell">
            <div className="profile-tabs">
              {!isExternalProfile ? (
                <button
                  type="button"
                  className={`tab-item ${activeTab === "general" ? "active" : ""}`}
                  onClick={() => setActiveTab("general")}
                >
                  <i className="fas fa-user"></i>
                  <span>Profile</span>
                </button>
              ) : null}

              <button
                type="button"
                className={`tab-item ${
                  activeTab === "studies" ? "active" : ""
                }`}
                onClick={() => setActiveTab("studies")}
              >
                <i className="fas fa-book"></i>
                <span>Studies</span>
              </button>

              <button
                type="button"
                className={`tab-item ${
                  activeTab === "documents" ? "active" : ""
                }`}
                onClick={() => setActiveTab("documents")}
              >
                <i className="fas fa-folder-open"></i>
                <span>{isOwnProfile ? "My Documents" : "Documents"}</span>
              </button>
              {!isExternalProfile && isSystemAdmin && (
                <button
                  type="button"
                  className={`tab-item ${
                    activeTab === "networking" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("networking")}
                >
                  <i className="fas fa-network-wired"></i>
                  <span>Networking</span>
                </button>
              )}
            </div>

            <div className="profile-tab-body">
            {activeTab === "networking" && isSystemAdmin ? (
              <div className="profile-tab-pane profile-tab-pane--networking">
                    <StaffLoginIpSection
                      organizationId={userData?.OrganizationId || ""}
                      profileStaffId={staffId || ""}
                      editorStaffId={userData?.StaffId || ""}
                      showAlert={showAlert}
                      readOnly={!isProfileUserActive}
                    />
              </div>
            ) : activeTab === "documents" ? (
              <div className="profile-tab-pane profile-tab-pane--documents">
                <UserProfileStaffDocuments
                  staffId={staffId}
                  organizationId={userData?.OrganizationId || ""}
                  createdBy={userData?.StaffId || ""}
                  studyOptions={studyList.map((s) => ({
                    label: s.label || s.StudyName || s.studyName || String(s.value || s.StudyId || ""),
                    value: s.value || s.StudyId || s.studyId,
                  }))}
                  canEdit={canEditDocuments}
                  showAlert={showAlert}
                />
              </div>
            ) : activeTab === "studies" ? (
              <div
                className={`profile-tab-pane profile-tab-pane--studies${
                  isExternalProfile ? " profile-tab-pane--studies-table" : ""
                }`}
              >
                {isExternalProfile ? (
                  <ExternalAssignedStudiesPanel
                    staffId={staffId}
                    canEdit={canEditAdminSections}
                    hideAssignToolbar={embedded}
                    assignedRows={externalScoping.getAssignedStudyReportRows()}
                    loading={externalScoping.assignedTableLoading}
                    renderAssignStep={externalScoping.renderStudiesStep}
                    onBeginAssign={externalScoping.beginAssignSession}
                    onCancelAssign={externalScoping.cancelAssignSession}
                    onCommitAssign={externalScoping.commitAssignSession}
                    onSaveAssign={() => handleSaveStudiesSection()}
                    getAssignShareError={
                      externalScoping.getStudyShareValidationError
                    }
                    onRemoveStudy={async (rowOrStudyId) => {
                      const nextStudies =
                        rowOrStudyId && typeof rowOrStudyId === "object"
                          ? externalScoping.unshareAssignedGrant(rowOrStudyId)
                          : externalScoping.unshareAssignedStudy(rowOrStudyId);
                      return handleSaveStudiesSection({ studies: nextStudies });
                    }}
                  />
                ) : (
                <div className="profile-studies-panel">
                  <div className="profile-studies-panel__body">
                    <StudyAssignmentDualList
                      options={studyList}
                      selected={
                        isSystemAdministratorProfile
                          ? mapStudyOptionsToAssignedRows(studyList)
                          : formData.studies || []
                      }
                      onChange={
                        isSystemAdministratorProfile
                          ? undefined
                          : handleStudiesChange
                      }
                      readOnly={
                        isSystemAdministratorProfile || !canEditAdminSections
                      }
                      readOnlyMessage={
                        isSystemAdministratorProfile
                          ? "System Administrator has access to all studies. Changes are not saved — access is automatic."
                          : "You do not have permission to update study assignments."
                      }
                    />
                  </div>
                  {fieldErrors.studies ? (
                    <div className="user-form-field-error">{fieldErrors.studies}</div>
                  ) : null}
                  {canEditAdminSections ? (
                    <div className="profile-studies-panel__footer user-modal-fold__footer">
                      <button
                        type="button"
                        className="btn btn-primary user-modal-fold__update-btn"
                        onClick={handleSaveStudiesSection}
                        disabled={isSystemAdministratorProfile}
                        title={
                          isSystemAdministratorProfile
                            ? "System Administrator has access to all studies — assignment is automatic"
                            : "Update studies only (does not change other user details)"
                        }
                      >
                        <i className="fas fa-save" aria-hidden="true" />
                        Update
                      </button>
                    </div>
                  ) : null}
                </div>
                )}
              </div>
            ) : !isExternalProfile && activeTab === "general" ? (
              <div className="profile-tab-pane profile-tab-pane--edit-layout">
                <InternalUserAddEditModal
                  open
                  variant="page"
                  formData={profileEditFormData}
                  fieldErrors={fieldErrors}
                  roles={roles}
                  visibleRoles={visibleRoles}
                  department={departments}
                  siteOptions={siteOptions}
                  study={studyList}
                  maxDate={maxDate}
                  userData={userData}
                  canEditUserPermissions={canEditUserPermissions}
                  canEditRole={canEditRole}
                  canEditAdminSections={canEditAdminSections}
                  onSave={handleProfileInlineSave}
                  onSaveAdditionalInfo={
                    canEditAdminSections && !isExternalProfile
                      ? handleSaveAdditionalSection
                      : null
                  }
                  onSaveRolePermissions={
                    canEditAdminSections ? handleSaveRolePermissionsSection : null
                  }
                  hideStudiesSection
                  hideRolePermissionSection={isExternalProfile}
                  permissionLayout="modules"
                  onFormInput={(e) => {
                    const name = e?.target?.name;
                    if (name) {
                      setFieldErrors((prev) => {
                        if (!prev?.[name]) return prev;
                        const next = { ...prev };
                        delete next[name];
                        return next;
                      });
                    }
                    handleFormInput(e);
                  }}
                  onDobChange={handlDOB}
                  onStartDateChange={handlStartDate}
                  onAccessDateChange={handlAccessDate}
                  onGenderChange={handleGenderChange}
                  onRoleSelect={(role) => {
                    setFieldErrors((prev) => {
                      if (!prev?.role) return prev;
                      const next = { ...prev };
                      delete next.role;
                      return next;
                    });
                    handleRoles(role);
                  }}
                  onUserPermissionsChange={handleProfileFormPermissionsChange}
                  onDepartmentSelect={handleDepartment}
                  onSitesSelect={(sites) => {
                    setFieldErrors((prev) => {
                      if (!prev?.selectedSites) return prev;
                      const next = { ...prev };
                      delete next.selectedSites;
                      return next;
                    });
                    handleSites(sites);
                  }}
                  onProfilePictureChange={(base64) =>
                    setFormData((prev) => ({ ...prev, profilePicture: base64 }))
                  }
                  showQmsTrainingAdditionalRoles={
                    orgHasQmsTrainingAdditionalRoles && !isExternalProfile
                  }
                  qmsTrainingAdditionalRoleOptions={qmsTrainingAdditionalRoleOptions}
                  onQmsTrainingAdditionalRolesSelect={handleQmsTrainingAdditionalRoles}
                  hideBasicIdentityFields

                />
              </div>
            ) : null}
            </div>
            </div>
          </div>
        </div>
      </main>

      <ConfirmModal
        isOpen={roleChangeConfirm.open}
        onClose={closeRoleChangeConfirm}
        title="Change role?"
        message="Changing the role may update this user's permissions. Do you want to apply the new role's permissions, or only change the role?"
        cancelText="Cancel"
        altConfirmText="Only role"
        onAltConfirm={confirmRoleOnly}
        altConfirmButtonClass="btn-outline-primary"
        confirmText="Apply permissions"
        onConfirm={confirmRoleWithPermissions}
        confirmButtonClass="btn-primary"
        icon="fas fa-user-tag text-warning"
      />

      <ConfirmModal
        isOpen={permissionSaveConfirmOpen}
        onClose={() => setPermissionSaveConfirmOpen(false)}
        title="Save permission changes?"
        message="Permissions have been changed for this user. Do you want to save these permission changes?"
        cancelText="Cancel"
        confirmText="Yes, save"
        onConfirm={confirmPermissionSave}
        confirmButtonClass="btn-primary"
        icon="fas fa-shield-alt text-warning"
      />

      <AdminChangeUserCredentialsModal
        open={adminCredForm.open}
        targetUser={adminCredForm.row}
        onClose={handleAdminCredFormClose}
        onSubmit={handleAdminCredFormSubmit}
      />

      <ConfirmModal
        isOpen={adminCredConfirm.open}
        onClose={handleAdminCredConfirmClose}
        onConfirm={handleAdminCredConfirmYes}
        title={
          adminCredConfirm.mode === "accessCode"
            ? "Change access code"
            : "Change password"
        }
        message={
          adminCredConfirm.row
            ? `Do you want to change the ${
                adminCredConfirm.mode === "accessCode" ? "access code" : "password"
              } for ${adminCredConfirm.row.Name || "this user"}?`
            : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass="btn-primary"
      />

      <AuthModal
        show={adminCredAuthOpen}
        onClose={handleAdminCredAuthClose}
        onSubmit={handleAdminCredAuthSubmit}
        showDescription={false}
      />

      <ConfirmModal
        isOpen={mfaResetConfirmOpen}
        onClose={() => setMfaResetConfirmOpen(false)}
        onConfirm={handleMfaResetConfirmYes}
        title="Reset MFA authenticator?"
        message={
          profileActionUser?.Name
            ? `Reset the authenticator app for ${profileActionUser.Name}? Use this when their phone was lost. They will sign in with email OTP next time and can register a new authenticator app.`
            : "Reset this user's authenticator app? They will sign in with email OTP next time and can register a new authenticator."
        }
        confirmText="Yes, reset"
        cancelText="Cancel"
        confirmButtonClass="btn-warning"
        icon="fas fa-mobile-alt text-warning"
      />

      <AuthModal
        show={mfaResetAuthOpen}
        onClose={handleMfaResetAuthClose}
        onSubmit={handleMfaResetAuthSubmit}
        showDescription={false}
      />

      <UserStaffDocumentAccessModal
        open={docAccessModal.open}
        mode={docAccessModal.mode}
        staffRow={docAccessModal.row}
        userData={userData}
        showLoader={showLoader}
        hideLoader={hideLoader}
        showAlert={showAlert}
        onClose={closeDocAccessModal}
      />

      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

export default UserProfile;
